import { useAuth } from '../AuthContext';
import AlberoRicorsi from './AlberoRicorsi';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import CaricaDocumenti from './CaricaDocumenti';
import { Search, Plus, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { getRicorsiCliente, creaGestioneRicorso, anteprimaCodiceRicorso, variantiCodice, getRicorrentiNellAtto, capofilaAutomatico } from '../api';
import API from '../api';
import SchedaCliente from './SchedaCliente';
import { msgErrore, lordoDaNetto, senzaRotella } from '../utils/formati';
import { GRADI, STATI_DEPOSITATI, STATI_PRIMA_DEL_DEPOSITO, tipiPerGrado } from '../utils/statiRicorso';
import { conScadenze, etichettaProvvedimento, giudizioPendente, tipiSelezionabili, udienzaFutura } from '../utils/provvedimenti';
import SezioniCollettivo, {
  BadgeCollettivo, BadgeStatoAdesione, EditorCampi, ModaleNuovoCliente, SceltaAderentiAppello, intestazioneCollettivo,
} from './Collettivi';

// Giorni per l'istanza di passaggio in giudicato (stessa regola del backend):
// 60 per TAR e Consiglio di Stato, 30 per Tribunale ordinario, Corte d'Appello o tribunale non indicato.
// Conta il tribunale della sentenza se presente, altrimenti quello del ricorso.
function giorniIstanzaGiudicato(ricorso, nomeTribunaleSentenza) {
  const nome = (nomeTribunaleSentenza || (ricorso && (ricorso.tribunale_sentenza || ricorso.tribunale)) || '').toUpperCase();
  return (nome.startsWith('TAR') || nome.includes('CONSIGLIO DI STATO')) ? 60 : 30;
}

// Data in formato gg/mm/aaaa per i badge di card (accetta 'YYYY-MM-DD' o ISO)
function fmtDataBadge(d) {
  if (!d || d === 'None') return '';
  const [y, m, g] = String(d).substring(0, 10).split('-');
  return (y && m && g) ? `${g}/${m}/${y}` : String(d);
}

// Un appello e' "di ottemperanza" (figlio dell'ottemperanza) se lo dice il tipo ricorso (titoli OTT o
// "ottemperanza" nel nome) oppure, in mancanza, il codice (titoli OTT a schema o codice provvisorio OTT...).
// Stessa regola del backend (_e_appello_di_ottemperanza).
function appelloDiOttemperanza(r) {
  const cod = r.codice_ricorso || '';
  return r.seg_titoli === 'OTT'
    || /ottemperanza/i.test(r.tipo || '')
    || cod.substring(7, 10) === 'OTT'
    || cod.startsWith('OTT');
}

// Direzione del collegamento dedotta dai gradi: 'padre' se `sel` e' a monte di `cur`, 'figlio' se a valle, null se ambiguo
function direzioneCollegamento(sel, cur) {
  const gs = sel.grado || 'primo_grado', gc = cur.grado || 'primo_grado';
  if (gs === gc) {
    // eccezione: l'estinto (-EST) e' padre della sua ripresentazione
    if (sel.codice_ricorso === cur.codice_ricorso + '-EST') return 'padre';
    if (cur.codice_ricorso === sel.codice_ricorso + '-EST') return 'figlio';
    return null;
  }
  const rango = { primo_grado: 0, appello: 1, ottemperanza: 1, ottemperanza_ottemperanza: 2 };
  const rs = rango[gs] ?? 1, rc = rango[gc] ?? 1;
  if (rs !== rc) return rs < rc ? 'padre' : 'figlio';
  // appello vs ottemperanza: l'appello di ottemperanza e' figlio dell'ottemperanza, altrimenti e' padre
  const appelloSel = gs === 'appello';
  const appelloFiglio = appelloDiOttemperanza(appelloSel ? sel : cur);
  return appelloSel ? (appelloFiglio ? 'figlio' : 'padre') : (appelloFiglio ? 'padre' : 'figlio');
}

// Generi di ricorso collegato proponibili e riconoscimento dei figli esistenti
const GENERI_COLLEGATI = {
  ott_ricorrente: {
    etichetta: 'ricorso di ottemperanza',
    haFiglio: (f) => ((f.tipo || '').toLowerCase().includes('ottemperanza')
      && !(f.tipo || '').toLowerCase().includes('spese'))
      || ((f.grado || '').startsWith('ottemperanza') && !(f.tipo || '').toLowerCase().includes('spese')),
  },
  ott_spese: {
    etichetta: 'ricorso di ottemperanza spese',
    haFiglio: (f) => (f.tipo || '').toLowerCase().includes('spese'),
  },
  appello: {
    etichetta: 'ricorso in appello',
    haFiglio: (f) => (f.grado || '') === 'appello'
      || (f.tipo || '').toLowerCase().startsWith('appello'),
  },
};

// Origine della scheda (report Campagne): obbligatoria in creazione, nessun valore predefinito
const ORIGINI = {
  nuova_adesione: 'Nuova adesione',
  trasloco: 'Trasloco storico',
  derivato: 'Derivato (ottemperanza / appello)',
};

function SelectOrigine({ value, onChange, bloccata, vuota, senzaNuova }) {
  return (
    <select className="input-filtro" style={{ width: '100%' }} value={value || ''} disabled={bloccata}
      onChange={e => onChange(e.target.value)}>
      {(vuota || !value) && <option value="">{vuota || '— scegli —'}</option>}
      {Object.entries(ORIGINI).filter(([k]) => !(senzaNuova && k === 'nuova_adesione'))
        .map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
}

// "Nuova adesione" solo sui tipi che contano come adesione (tipi_ricorso.conta_come_adesione)
function tipoNonConta(tipi, tipoId) {
  return (tipi || []).find(t => String(t.id) === String(tipoId))?.conta_come_adesione === false;
}

function generiProponibili(ricorso, figli) {
  const out = [];
  const figliOk = figli || [];
  if (ricorso.adesione_ottemperanza && !figliOk.some(GENERI_COLLEGATI.ott_ricorrente.haFiglio))
    out.push('ott_ricorrente');
  if (ricorso.stato_ottemperanza_spese === 'Pronta' && !figliOk.some(GENERI_COLLEGATI.ott_spese.haFiglio))
    out.push('ott_spese');
  if (ricorso.ricorso_in_appello === 'Sì' && !figliOk.some(GENERI_COLLEGATI.appello.haFiglio))
    out.push('appello');
  return ricorso.collettivo ? out.filter(g => g === 'appello') : out;   // ottemperanza dei collettivi: fuori ambito
}

// grado: quello scelto nel form, che il nuovo tipo eredita. Restituisce { id, grado } (un tipo che
// esisteva già torna con il suo grado) o null.
async function creaTipoAlVolo(apiRef, aggiornaTipi, grado = 'primo_grado') {
  const etichettaGrado = (GRADI.find(([g]) => g === grado) || [null, grado])[1];
  const nome = window.prompt(`Nome del nuovo tipo ricorso (grado: ${etichettaGrado}):`);
  if (!nome || !nome.trim()) return null;
  const conta = window.confirm(`Il tipo "${nome.trim()}" conta come adesione (ricorso a cui i clienti aderiscono)?\n\nOK = sì, Annulla = no`);
  try {
    const r = await apiRef.post('/finanziario/tipi-ricorso', { nome: nome.trim(), conta_come_adesione: conta, grado });
    if (aggiornaTipi) await aggiornaTipi();
    return { id: r.data.id, grado: r.data.grado || grado };
  } catch (e) {
    window.alert(e.response?.data?.detail || 'Errore nella creazione del tipo.');
    return null;
  }
}

const CLIENTE_STUDIO = 47832;

// capofila: scheda collettiva. Con la scheda salvata il capofila si sceglie tra i ricorrenti attivi
// (diventa "scelto a mano"); manuale = gestione_ricorso.capofila_manuale.
function IntestatarioBox({ cliente, gestioneId, onCambiato, capofila, bloccato, manuale }) {
  const [cambio, setCambio] = useState(false);
  const [q, setQ] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [carico, setCarico] = useState(false);
  const [msg, setMsg] = useState(null);
  const traRicorrenti = capofila && !!gestioneId;
  // "Nuovo cliente" (stessa modale e permesso del riquadro "Aggiungi ricorrente"): intestatario,
  // primo ricorrente del form nuovo e "Cambia intestatario"; non in "Cambia capofila", che sceglie
  // solo tra i ricorrenti della scheda
  const { haPermesso } = useAuth();
  const [nuovoCliente, setNuovoCliente] = useState(false);
  const puoCreare = !traRicorrenti && haPermesso('clienti.crea');
  const pulsanteNuovo = puoCreare && (
    <button type="button" onClick={() => setNuovoCliente(true)}
      style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
        border: '1px solid #276749', background: 'white', color: '#276749', fontWeight: 600 }}>
      Nuovo cliente
    </button>
  );
  const modaleNuovo = nuovoCliente && (
    <ModaleNuovoCliente onChiudi={() => setNuovoCliente(false)}
      onCreato={(c) => { setNuovoCliente(false); assegna(c); }} />
  );
  const cerca = async (testo) => {
    setCarico(true);
    if (traRicorrenti) {
      try {
        const t = (testo || '').trim().toLowerCase();
        const r = await getRicorrentiNellAtto(gestioneId);
        setRisultati(r.data.filter(a => !a.capofila
          && (!t || `${a.cognome} ${a.nome} ${a.codice_fiscale || ''}`.toLowerCase().includes(t))));
      } catch (e) { setRisultati([]); } finally { setCarico(false); }
      return;
    }
    try {
      const r = await API.get(`/clienti?q=${encodeURIComponent(testo)}&per_pagina=8`);
      const dati = Array.isArray(r.data) ? r.data
        : (Array.isArray(r.data?.clienti) ? r.data.clienti
        : (Array.isArray(r.data?.risultati) ? r.data.risultati : []));
      setRisultati(dati);
    } catch (e) { setRisultati([]); }
    finally { setCarico(false); }
  };
  const assegna = async (nuovo) => {
    if (!gestioneId) {
      // form nuovo: la scelta vale solo localmente
      onCambiato && onCambiato(nuovo);
      setCambio(false); setQ(''); setRisultati([]);
      return;
    }
    const chi = `${nuovo.cognome || nuovo.ragione_sociale} ${nuovo.nome || ''}`;
    if (!window.confirm(capofila ? `Nuovo capofila scelto a mano: ${chi}? Il capofila attuale resta tra i ricorrenti.`
      : `Riassegnare il ricorso a ${chi}?`)) return;
    try {
      await API.patch(`/finanziario/gestione-ricorso/${gestioneId}/intestatario`,
        { codice_cliente: nuovo.codice_cliente });
      setMsg('Intestatario aggiornato.');
      setCambio(false); setQ(''); setRisultati([]);
      onCambiato && onCambiato(nuovo);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Errore nella riassegnazione.');
    }
  };
  const tornaAutomatico = async () => {
    try {
      await capofilaAutomatico(gestioneId);
      setMsg('Capofila automatico: primo in ordine alfabetico.');
      onCambiato && onCambiato();
    } catch (e) {
      setMsg(msgErrore(e, 'Capofila non aggiornato.'));
    }
  };
  if (!cliente) {
    // Nuovo ricorso aperto senza cliente selezionato: si sceglie qui l'intestatario
    return (
      <div style={{ background: '#FFF7ED', border: '1px solid #C05621', borderRadius: 8,
        padding: '10px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: '#C05621', textTransform: 'uppercase',
          letterSpacing: '0.5px', marginBottom: 6, fontWeight: 600 }}>{capofila ? 'Primo ricorrente *' : 'Intestatario del ricorso *'}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input className="input-filtro" style={{ flex: 1 }} autoFocus
            placeholder="Cerca cliente per cognome, nome o codice fiscale..."
            value={q}
            onChange={e => { setQ(e.target.value); if (e.target.value.trim().length >= 2) cerca(e.target.value); else setRisultati([]); }} />
          {pulsanteNuovo}
        </div>
        {modaleNuovo}
        <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
          {carico && <div style={{ fontSize: 12, color: '#a0aec0', padding: 6 }}>Ricerca…</div>}
          {!carico && risultati.map(r => (
            <div key={r.codice_cliente} onClick={() => assegna(r)}
              style={{ padding: '6px 10px', cursor: 'pointer', borderRadius: 6,
                display: 'flex', justifyContent: 'space-between', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontWeight: 600 }}>
                {r.ragione_sociale || `${r.cognome || ''} ${r.nome || ''}`}
              </span>
              <span style={{ fontSize: 12, color: '#718096' }}>{r.codice_fiscale || r.localita || ''}</span>
            </div>
          ))}
          {!carico && q.trim().length >= 2 && !risultati.length && (
            <div style={{ fontSize: 12, color: '#a0aec0', padding: 6 }}>Nessun cliente trovato.</div>
          )}
        </div>
      </div>
    );
  }
  const nome = cliente.ragione_sociale || `${cliente.cognome || ''} ${cliente.nome || ''}`.trim();
  return (
    <div style={{ background: '#EBF4FF', border: '1px solid #2E75B6', borderRadius: 8,
      padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={{ fontSize: 11, color: '#2E75B6', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginRight: 8 }}>{capofila ? (gestioneId ? 'Capofila' : 'Primo ricorrente') : 'Intestato a'}</span>
          <span style={{ fontWeight: 700, color: '#1A1A2E' }}>{nome}</span>
          {capofila && (
            <span style={{ fontSize: 11, color: '#4A5568', marginLeft: 8, fontStyle: 'italic' }}>
              {!gestioneId ? 'il capofila sarà il primo in ordine alfabetico tra i ricorrenti'
                : manuale ? 'scelto a mano' : 'proposto automaticamente (primo in ordine alfabetico)'}</span>
          )}
          {cliente.codice_fiscale && (
            <span style={{ fontSize: 12, color: '#4A5568', marginLeft: 8 }}>CF: {cliente.codice_fiscale}</span>
          )}
          {cliente.codice_cliente === CLIENTE_STUDIO && (
            <span style={{ fontSize: 11, background: '#FFF7ED', color: '#C05621',
              border: '1px solid #C05621', borderRadius: 4, padding: '1px 6px', marginLeft: 8 }}>STUDIO</span>
          )}
        </div>
        {bloccato && <span style={{ fontSize: 11, color: '#718096' }}>ricorso depositato: il capofila non si cambia</span>}
        {traRicorrenti && manuale && !bloccato && !cambio && (
          <button type="button" onClick={tornaAutomatico}
            style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: '1px dashed #2E75B6', background: 'white', color: '#2E75B6', fontWeight: 600 }}>
            Torna al primo in ordine alfabetico
          </button>
        )}
        {!cambio && !bloccato && (
          <button type="button" onClick={() => { setCambio(true); setMsg(null); cerca(''); }}
            style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: '1px dashed #2E75B6', background: 'white', color: '#2E75B6', fontWeight: 600 }}>
            {capofila ? (gestioneId ? '⇄ Cambia capofila' : '⇄ Cambia primo ricorrente') : '⇄ Cambia intestatario'}
          </button>
        )}
      </div>
      {msg && <div style={{ fontSize: 12, color: '#276749', marginTop: 6 }}>{msg}</div>}
      {cambio && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input className="input-filtro" style={{ flex: 1 }} autoFocus
              placeholder="Cerca cliente per cognome, nome o codice fiscale..."
              value={q}
              onChange={e => { setQ(e.target.value); cerca(e.target.value); }} />
            {pulsanteNuovo}
          </div>
          {modaleNuovo}
          <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
            {carico && <div style={{ fontSize: 12, color: '#a0aec0', padding: 6 }}>Ricerca…</div>}
            {!carico && risultati.map(r => (
              <div key={r.codice_cliente} onClick={() => assegna(r)}
                style={{ padding: '6px 10px', cursor: 'pointer', borderRadius: 6,
                  display: 'flex', justifyContent: 'space-between', gap: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontWeight: 600 }}>
                  {r.ragione_sociale || `${r.cognome || ''} ${r.nome || ''}`}
                </span>
                <span style={{ fontSize: 12, color: '#718096' }}>{r.codice_fiscale || r.localita || ''}</span>
              </div>
            ))}
            {!carico && !risultati.length && (
              <div style={{ fontSize: 12, color: '#a0aec0', padding: 6 }}>Nessun risultato.</div>
            )}
          </div>
          <button type="button" onClick={() => { setCambio(false); setQ(''); }}
            style={{ marginTop: 6, background: 'none', border: '1px solid #CBD5E0', borderRadius: 6,
              padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#718096' }}>Annulla</button>
        </div>
      )}
    </div>
  );
}

// Grado e tipo nel form ricorso (richiesta del 23/10): il grado filtra i tipi (tipi_ricorso.grado);
// scelto un tipo il grado è il suo; cambiando grado un tipo di un altro grado si svuota.
function campiGradoTipo(form, set, tipi, onNuovoTipo, assegnato = null) {
  const cambiaGrado = (g) => {
    set('grado', g);
    const t = tipi.find(x => String(x.id) === String(form.tipo_ricorso_id));
    if (t && (t.grado || 'primo_grado') !== g) set('tipo_ricorso_id', null);
  };
  return {
    grado: (
      <select className="input-filtro" style={{ width: '100%' }} aria-label="Grado ricorso"
        value={form.grado || 'primo_grado'} onChange={e => cambiaGrado(e.target.value)}>
        {GRADI.map(([g, etichetta]) => <option key={g} value={g}>{etichetta}</option>)}
      </select>
    ),
    tipo: (
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <SelectField value={form.tipo_ricorso_id}
            onChange={v => {
              set('tipo_ricorso_id', v);
              const g = tipi.find(t => String(t.id) === String(v))?.grado;
              if (g) set('grado', g);
            }}
            options={tipiPerGrado(tipi, form.grado, assegnato)} placeholder="Seleziona tipo..." />
        </div>
        {onNuovoTipo && (
          <button type="button" title="Crea nuovo tipo (con il grado scelto)"
            onClick={async () => {
              const t = await onNuovoTipo(form.grado || 'primo_grado');
              if (t) { set('tipo_ricorso_id', t.id); if (t.grado) set('grado', t.grado); }
            }}
            style={{ border: '1px dashed #2E75B6', background: 'white', color: '#2E75B6',
              borderRadius: 6, padding: '0 10px', cursor: 'pointer', fontWeight: 700 }}>＋</button>
        )}
      </div>
    ),
  };
}


// ── Componenti statici (FUORI da ogni funzione per evitare bug focus) ─────────

const LabelInput = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, color: '#718096', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    {children}
  </div>
);

export const CONFERMA_DEPOSITO = 'Il deposito è definitivo: dopo non si potrà annullare. Confermi?';
// collettivo: capofila e nome che il ricorso avrà dal deposito (denominazione_proposta del dettaglio,
// calcolata dal backend con la stessa funzione che la salva)
export const confermaDepositoCollettivo = (ricorso, nome) => 'Il deposito è definitivo: dopo non si potranno aggiungere '
  + `ricorrenti né annullarlo. Capofila: ${`${(ricorso.cognome || '').toUpperCase()} ${ricorso.nome_cliente || ricorso.nome || ''}`.trim()}. `
  + `Nome del ricorso: ${nome || '—'}`;

const BadgeStato = ({ stato }) => {
  const colori = {
    'in_lavorazione': { bg: '#EBF4FF', color: '#2E75B6' },
    'da_depositare':  { bg: '#FAEEDA', color: '#633806' },
    'depositato':     { bg: '#E2EFDA', color: '#276749' },
    'chiuso':         { bg: '#F2F2F2', color: '#718096' },
    'estinto':        { bg: '#FCECEA', color: '#C53030' },
    'estinto_e_ripresentato': { bg: '#FAEEDA', color: '#633806' },
    'revocato':       { bg: '#FCECEA', color: '#9B2C2C' },
  };
  const c = colori[stato] || colori['in_lavorazione'];
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, ...c }}>
      {stato?.replace(/_/g, ' ')}
    </span>
  );
};

// giudizio pendente (27/10): scheda depositata senza una Sentenza o un Dispositivo con numero
const BadgePendente = ({ ricorso }) => (giudizioPendente(ricorso) ? (
  <span title="Nessuna sentenza o dispositivo con numero"
    style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#FFF7ED', color: '#C05621',
      border: '1px solid #FBD38D' }}>Pendente</span>
) : null);

const SelectField = ({ value, onChange, options, placeholder, labelKey = 'nome', valueKey = 'id' }) => (
  <select className="input-filtro" style={{ width: '100%' }}
    value={value || ''}
    onChange={e => onChange(e.target.value ? parseInt(e.target.value) : null)}>
    <option value="">{placeholder}</option>
    {options.map(o => (
      <option key={o[valueKey]} value={o[valueKey]}>{o[labelKey]}</option>
    ))}
  </select>
);

const Campo = ({ label, value, highlight, nota }) => {
  if (value === null || value === undefined || value === '' || value === '—') return null;
  return (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 11, color: '#718096', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    <div style={{ fontSize: 13, color: highlight ? '#c53030' : '#1A1A2E', fontWeight: highlight ? 500 : 400,
      ...(nota && value ? { padding: '5px 8px', borderRadius: 5, background: '#FFFBF0', border: '1px solid #F6E05E' } : {}) }}>{value}</div>
  </div>
  );
};

// ── Autocompletamento tribunali ───────────────────────────────────────────────

// Filtro "Tipo ricorso" della ricerca (23/10): si scrive e la lista dei tipi si restringe, come
// l'autocomplete dei tribunali, ma sui tipi già caricati (anche i disattivati, per i ricorsi
// vecchi). Il filtro vale solo per un tipo scelto dalla lista: value/onChange = nome del tipo o ''.
export function AutocompleteTipo({ tipi, value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const t = query.trim().toLowerCase();
  const risultati = tipi.filter(x => !t || (x.nome || '').toLowerCase().includes(t));
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <input className="input-filtro" style={{ width: '100%' }} value={query} placeholder="Tipo ricorso"
        aria-label="Filtro tipo ricorso" title="Scrivi per restringere la lista, poi scegli il tipo"
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (value) onChange(''); }} />
      {open && risultati.length > 0 && (
        <div role="listbox" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'white', border: '1px solid #dde1e7', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto'
        }}>
          {risultati.map(x => (
            <div key={x.id} role="option" aria-selected={x.nome === value}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
              onMouseDown={() => { setQuery(x.nome); onChange(x.nome); setOpen(false); }}>
              {x.nome}{x.attivo === false && <span style={{ color: '#718096', marginLeft: 8, fontSize: 11 }}>disattivato</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AutocompleteTribunali({ value, onChange }) {
  const [query, setQuery] = useState(value?.nome || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const timerRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (!v) { onChange(null); setResults([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (v.length >= 2) {
        const r = await API.get(`/finanziario/tribunali?q=${v}`);
        setResults(r.data);
        setOpen(true);
      }
    }, 300);
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <input className="input-filtro" style={{ width: '100%' }}
        value={query} onChange={handleChange} placeholder="Cerca tribunale..." />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'white', border: '1px solid #dde1e7', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto'
        }}>
          {results.map(t => (
            <div key={t.id}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
              onMouseDown={() => { setQuery(t.nome); onChange(t); setOpen(false); }}>
              <span style={{ fontWeight: 500 }}>{t.nome}</span>
              <span style={{ color: '#718096', marginLeft: 8, fontSize: 11 }}>{t.tipo}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ricorso collettivo nei form (docs/SPEC_ricorsi_collettivi.md §4) ──────────
// Nei collettivi il cliente scelto è il primo ricorrente (capofila automatico o scelto a mano), i prezzi sono due (pieno e convenzionati, con la
// stessa "Cifra al netto") e compenso pagato e convenzione sono dati del singolo ricorrente
// (card Ricorrenti). Le regole (due prezzi, adesioni storiche, flag con più adesioni) le
// controlla il backend: i suoi messaggi passano da msgErrore.
function CasellaCollettivo({ form, set, bloccata }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, color: '#1A1A2E' }}>
      <input type="checkbox" checked={!!form.collettivo} disabled={bloccata} onChange={e => set('collettivo', e.target.checked)} />
      <b>Ricorso collettivo</b>
      <span style={{ fontSize: 12, color: '#718096' }}>{bloccata ? "appello di un ricorso collettivo: è collettivo anche l'appello"
        : 'un solo atto per più ricorrenti: il cliente è il primo ricorrente, il capofila è il primo in ordine alfabetico'}</span>
    </label>
  );
}

// Campi del collettivo nel corpo del salvataggio. In modifica `collettivo` va solo se cambia
// (il form rimanda tutti i campi a ogni salvataggio).
function campiCollettivo(cleanForm, ricorso) {
  const cambia = !ricorso || !!cleanForm.collettivo !== !!ricorso.collettivo;
  const out = { collettivo: cambia ? !!cleanForm.collettivo : undefined };     // undefined = non inviato
  if (cleanForm.collettivo) {
    const c = cleanForm.compenso_convenzionati;
    out.compenso_convenzionati = c === null || c === undefined ? null : (cleanForm.flag_netto ? lordoDaNetto(c) : parseFloat(c));
    out.compenso_pagato = undefined;
  } else if (ricorso && ricorso.compenso_convenzionati !== null && ricorso.compenso_convenzionati !== undefined) {
    out.compenso_convenzionati = null;          // tornato individuale: niente prezzo convenzionati
  } else {
    out.compenso_convenzionati = undefined;
  }
  return out;
}

// ── Form nuovo ricorso ────────────────────────────────────────────────────────

function FormNuovoRicorso({ cliente, tipi, avvocati, parti, onSave, onCancel, iniziale, onNuovoTipo, padreCollettivo }) {
  const [intestatario, setIntestatario] = useState(cliente);
  const [aderentiAppello, setAderentiAppello] = useState(null);     // null = tutti i depositati del padre
  const [campiNuovi, setCampiNuovi] = useState([]);                  // campi personalizzati del collettivo (etichette)
  const [form, setForm] = useState({
    codice_ricorso: '',
    codice_esolver: '',
    tipo_ricorso_id: null,
    grado: 'primo_grado',
    data_registrazione: '',
    data_adesione: '',
    compenso: '100',
    flag_netto: false,
    compenso_pagato: false,
    stato: 'in_lavorazione',
    parte_resistente_id: parti.find(p => p.is_default)?.id || null,
    legale_studio_id: null,
    tribunale_id: null,
    numero_rg: '',
    contributo_unificato: '',
    cu_esente: false,
    cu_f24_pagato: false,
    tipo_udienza: '',
    sezione: '',
    diffida_spese: '',
    data_udienza: '',
    comunicata: false,
    note: '',
    origine: '',
    collettivo: false,
    compenso_convenzionati: '',
    ...(iniziale || {})
  });
  const [tribunale, setTribunale] = useState(null);
  const [carico, setCarico] = useState(false);
  const [errore, setErrore] = useState(null);

  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
  const gradoTipo = campiGradoTipo(form, set, tipi, onNuovoTipo);
  const senzaNuova = tipoNonConta(tipi, form.tipo_ricorso_id);
  useEffect(() => {
    if (senzaNuova && form.origine === 'nuova_adesione') set('origine', '');
  }, [senzaNuova, form.origine, set]);
  // un collettivo si crea non depositato: i ricorrenti si aggiungono prima del deposito
  useEffect(() => {
    if (form.collettivo && STATI_DEPOSITATI.includes(form.stato)) { set('stato', 'in_lavorazione'); set('data_adesione', ''); }
  }, [form.collettivo, form.stato, set]);

  // Sincronizzazione codice_ricorso ↔ codice_esolver
  const handleCodiceEsolver = useCallback((v) => {
    setForm(f => ({ ...f, codice_esolver: v, codice_ricorso: v }));
  }, []);

  const handleCodiceRicorso = useCallback((v) => {
    setForm(f => ({ ...f, codice_ricorso: v }));
  }, []);

  // ── Codice generato dall'app (schema Esolver) ──
  const [codiceManuale, setCodiceManuale] = useState(false);
  const [anteprima, setAnteprima] = useState(null);
  const [avanzate, setAvanzate] = useState(false);
  const [varianti, setVarianti] = useState({ RICORS: [], TITOL: [], COMP: [] });
  const [seg, setSeg] = useState({ seg_tipo: '', seg_titoli: '', seg_comp: '', zona: '' });
  const [descrizione, setDescrizione] = useState('');
  const [descrizioneToccata, setDescrizioneToccata] = useState(false);
  useEffect(() => { if (!descrizioneToccata) setDescrizione(anteprima?.descrizione || ''); }, [anteprima, descrizioneToccata]);
  useEffect(() => {
    if (!avanzate || varianti.RICORS.length) return;
    variantiCodice().then(r => {
      const g = { RICORS: [], TITOL: [], COMP: [] };
      (r.data || []).forEach(v => { if (g[v.tipo_variante]) g[v.tipo_variante].push(v); });
      setVarianti(g);
    }).catch(() => {});
  }, [avanzate, varianti.RICORS.length]);
  useEffect(() => {
    if (codiceManuale) return;
    const tid = form.tipo_ricorso_id, trid = tribunale?.id;
    if (!tid || !trid) { setAnteprima(null); return; }
    const params = { tipo_ricorso_id: tid, tribunale_id: trid };
    Object.entries(seg).forEach(([k, v]) => { if (v) params[k] = v; });
    let attivo = true;
    anteprimaCodiceRicorso(params)
      .then(r => { if (attivo) { setAnteprima(r.data); setErrore(null); } })
      .catch(e => { if (attivo) { setAnteprima(null); setErrore(msgErrore(e, 'Impossibile generare il codice')); } });
    return () => { attivo = false; };
  }, [form.tipo_ricorso_id, tribunale?.id, seg, codiceManuale]);
  const salva = async () => {
    if (!intestatario?.codice_cliente) { setErrore('Scegli il cliente intestatario del ricorso'); return; }
    if (!form.origine) { setErrore("Indica l'origine della scheda"); return; }
    if (codiceManuale && !form.codice_ricorso) { setErrore('Codice ricorso obbligatorio'); return; }
    if (!codiceManuale && (!form.tipo_ricorso_id || !tribunale?.id)) { setErrore('Per generare il codice servono tipo ricorso e tribunale'); return; }
    const nomeTipoSel = (tipi.find(t => String(t.id) === String(form.tipo_ricorso_id))?.nome || '').toLowerCase();
    const tipoDaCliente = nomeTipoSel.includes('ottemperanza ricorrente') || nomeTipoSel.includes('carta docente');
    if (tipoDaCliente && intestatario?.codice_cliente === CLIENTE_STUDIO) {
      if (!window.confirm('Stai intestando allo STUDIO un tipo che di norma va al cliente. Confermi?')) return;
    }
    setCarico(true); setErrore(null);
    const cleanForm = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      await onSave({
        ...cleanForm,
        _intestatario: intestatario?.codice_cliente || null,
        _intestatarioObj: intestatario || null,
        codice_ricorso: codiceManuale ? cleanForm.codice_ricorso : null,
        codice_esolver: codiceManuale ? cleanForm.codice_esolver : null,
        ...(codiceManuale ? {} : Object.fromEntries(Object.entries(seg).filter(([, v]) => v))),
        descrizione_ricorso: descrizione || null,
        tribunale_id: tribunale?.id || null,
        compenso: cleanForm.compenso ? (cleanForm.flag_netto ? lordoDaNetto(cleanForm.compenso) : parseFloat(cleanForm.compenso)) : null,
        flag_netto: undefined,
        contributo_unificato: cleanForm.contributo_unificato ? parseFloat(cleanForm.contributo_unificato) : 0,
        ...campiCollettivo(cleanForm),
        ...(padreCollettivo && aderentiAppello ? { aderenti: aderentiAppello } : {}),
        // l'appello di un collettivo copia i campi del primo grado (backend)
        ...(cleanForm.collettivo && !padreCollettivo && campiNuovi.some(c => c.etichetta.trim())
          ? { campi_personalizzati: campiNuovi.map(c => c.etichetta.trim()).filter(Boolean) } : {}),
      });
    } catch(e) {
      setErrore(msgErrore(e, 'Errore nel salvataggio'));
    } finally {
      setCarico(false);
    }
  };

  return (
    <div style={{ background: '#F7F8FA', border: '1px solid #dde1e7', borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A2E', marginBottom: 16 }}>
        Nuovo ricorso{intestatario ? ` — ${intestatario.ragione_sociale || `${intestatario.cognome || ''} ${intestatario.nome || ''}`.trim()}` : ''}
      </div>

      {errore && (
        <div className="alert errore" style={{ marginBottom: 12 }}>
          <AlertCircle size={14} /><span>{errore}</span>
        </div>
      )}

      <CasellaCollettivo form={form} set={set} bloccata={!!padreCollettivo} />
      {/* appello di un collettivo: il capofila è il primo in ordine alfabetico tra i ricorrenti scelti */}
      {!padreCollettivo && (
        <IntestatarioBox cliente={intestatario} gestioneId={null} onCambiato={(n) => setIntestatario(n)} capofila={form.collettivo} />
      )}
      {padreCollettivo && (
        <SceltaAderentiAppello padreId={padreCollettivo} onChange={setAderentiAppello}
          onCapofila={(c) => setIntestatario(c || cliente)} />
      )}
      {form.collettivo && !padreCollettivo && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#C05621', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
            Campi personalizzati dei ricorrenti</div>
          <div style={{ fontSize: 11, color: '#718096', marginBottom: 6 }}>
            Campi di testo da compilare per ogni ricorrente (es. Classe di concorso, Provincia); si cambiano fino al deposito.</div>
          <EditorCampi campi={campiNuovi} onChange={setCampiNuovi} />
        </div>
      )}

      {/* DATI RICORSO */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#2E75B6', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #2E75B620' }}>
        Dati ricorso
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ gridColumn: '1 / span 2' }}>
          <LabelInput label={codiceManuale ? "Codice ricorso (inserito a mano) *" : "Codice ricorso (generato dall'app)"}>
            {codiceManuale ? (
              <input className="input-filtro" style={{ width: '100%', fontFamily: 'Consolas, monospace' }}
                value={form.codice_ricorso}
                onChange={e => { handleCodiceRicorso(e.target.value); handleCodiceEsolver(e.target.value); }}
                placeholder="es. IND2026PDCTRBNA1T260908" />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 34, padding: '4px 10px',
                background: '#fff', border: '1px solid #dde1e7', borderRadius: 6 }}>
                <span style={{ fontFamily: 'Consolas, monospace', fontWeight: 700, color: anteprima ? '#1A1A2E' : '#A0AEC0' }}>
                  {anteprima ? anteprima.codice : 'Scegli tipo ricorso e tribunale'}
                </span>
              </div>
            )}
            <input className="input-filtro" style={{ width: '100%', marginTop: 6 }}
              value={descrizione}
              onChange={e => { setDescrizione(e.target.value); setDescrizioneToccata(true); }}
              placeholder="Descrizione del ricorso (suggerita dall'app, modificabile)" />
            {descrizioneToccata && anteprima && descrizione !== anteprima.descrizione && (
              <button type="button" onClick={() => setDescrizioneToccata(false)}
                style={{ background: 'none', border: 'none', color: '#718096', cursor: 'pointer', padding: 0, fontSize: 11, marginTop: 2 }}>
                Ripristina descrizione suggerita
              </button>
            )}
            <div style={{ display: 'flex', gap: 14, marginTop: 4, fontSize: 11 }}>
              <button type="button" onClick={() => setCodiceManuale(m => !m)}
                style={{ background: 'none', border: 'none', color: '#2E75B6', cursor: 'pointer', padding: 0 }}>
                {codiceManuale ? 'Torna al codice generato' : 'Inserisci il codice a mano'}
              </button>
              {!codiceManuale && (
                <button type="button" onClick={() => setAvanzate(a => !a)}
                  style={{ background: 'none', border: 'none', color: '#718096', cursor: 'pointer', padding: 0 }}>
                  {avanzate ? 'Nascondi segmenti' : 'Modifica segmenti'}
                </button>
              )}
            </div>
            {!codiceManuale && avanzate && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 8, padding: 10,
                background: '#EEF2F7', borderRadius: 6 }}>
                {[['seg_tipo', 'Tipo', 'RICORS'], ['seg_titoli', 'Titoli', 'TITOL'], ['seg_comp', 'Competenza', 'COMP']].map(([k, lab, tv]) => (
                  <label key={k} style={{ fontSize: 11, color: '#4A5568' }}>{lab}
                    <select className="input-filtro" style={{ width: '100%' }} value={seg[k]}
                      onChange={e => setSeg(s => ({ ...s, [k]: e.target.value }))}>
                      <option value="">(automatico{anteprima ? ': ' + anteprima.segmenti[{seg_tipo:'tipo',seg_titoli:'titoli',seg_comp:'competenza'}[k]] : ''})</option>
                      {varianti[tv].map(v => <option key={v.codice} value={v.codice}>{v.codice} — {v.descrizione}</option>)}
                    </select>
                  </label>
                ))}
                <label style={{ fontSize: 11, color: '#4A5568' }}>Provincia
                  <input className="input-filtro" style={{ width: '100%' }} maxLength={2} value={seg.zona}
                    placeholder={anteprima ? anteprima.segmenti.zona : 'NA'}
                    onChange={e => setSeg(s => ({ ...s, zona: e.target.value.toUpperCase() }))} />
                </label>
              </div>
            )}
          </LabelInput>
        </div>
        <LabelInput label="Grado ricorso">{gradoTipo.grado}</LabelInput>
        <LabelInput label="Origine scheda">
          <SelectOrigine value={form.origine} onChange={v => set('origine', v)}
            bloccata={iniziale?.origine === 'derivato'} senzaNuova={senzaNuova} />
        </LabelInput>
        <LabelInput label="Tipo ricorso">{gradoTipo.tipo}</LabelInput>
        <LabelInput label="Avvocato studio">
          <SelectField value={form.legale_studio_id}
            onChange={v => set('legale_studio_id', v)}
            options={avvocati} placeholder="Seleziona avvocato..." />
        </LabelInput>
        <LabelInput label="Data registrazione adesione">
          <input type="date" className="input-filtro" style={{ width: '100%' }}
            value={form.data_registrazione}
            onChange={e => set('data_registrazione', e.target.value)} />
        </LabelInput>
        <LabelInput label="Stato">
          <select className="input-filtro" style={{ width: '100%' }}
            value={form.stato} onChange={e => {
              const s = e.target.value;
              set('stato', s);
              if (s !== 'depositato') set('data_adesione', '');
            }}>
            <option value="in_lavorazione">In lavorazione</option>
            <option value="da_depositare">Da depositare</option>
            <option value="depositato" disabled={!!form.collettivo}
              title={form.collettivo ? 'Un ricorso collettivo si crea non depositato: aggiungi i ricorrenti, poi depositalo' : undefined}>
              Depositato</option>
          </select>
        </LabelInput>
        {form.stato === 'depositato' && (
          <LabelInput label="Data deposito">
            <input type="date" className="input-filtro" style={{ width: '100%' }}
              value={form.data_adesione}
              onChange={e => set('data_adesione', e.target.value)} />
          </LabelInput>
        )}
      </div>
      {/* PARTI */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8E44AD', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #8E44AD20' }}>
        Parti
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Parte resistente">
          <SelectField value={form.parte_resistente_id}
            onChange={v => set('parte_resistente_id', v)}
            options={parti} placeholder="Seleziona..." />
        </LabelInput>
      </div>
      {/* COMPENSO */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#276749', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #27674920' }}>
        Compenso
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Compenso (€)">
          <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: '100%' }}
            value={form.compenso}
            onChange={e => set('compenso', e.target.value)} />
        </LabelInput>
        {form.collettivo && (
          <LabelInput label="Compenso convenzionati (€)">
            <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: '100%' }}
              value={form.compenso_convenzionati}
              onChange={e => set('compenso_convenzionati', e.target.value)} />
          </LabelInput>
        )}
        <LabelInput label="Cifra al netto">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34 }}>
            <input type="checkbox" checked={form.flag_netto || false}
              onChange={e => set('flag_netto', e.target.checked)} />
            <span style={{ fontSize: 12, color: '#4a5568' }}>
              {form.flag_netto && form.compenso
                ? `Lordo: ${lordoDaNetto(form.compenso).toFixed(2)} €`
                  + (form.collettivo && form.compenso_convenzionati ? ` / ${lordoDaNetto(form.compenso_convenzionati).toFixed(2)} €` : '')
                : 'Spunta se la cifra è netta'}
            </span>
          </div>
        </LabelInput>
        {!form.collettivo && <LabelInput label="Compenso pagato">
          <select className="input-filtro" style={{ width: '100%' }}
            value={form.compenso_pagato ? 'si' : 'no'}
            onChange={e => set('compenso_pagato', e.target.value === 'si')}>
            <option value="no">❌ No</option>
            <option value="si">✅ Sì</option>
          </select>
        </LabelInput>}
      </div>
      {/* PROCEDURA */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#E67E22', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #E67E2220' }}>
        Procedura
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 }}>
        <LabelInput label="Tribunale">
          <AutocompleteTribunali value={tribunale}
            onChange={t => { setTribunale(t); set('tribunale_id', t?.id || null); }} />
        </LabelInput>
        <LabelInput label="Sezione">
          <input className="input-filtro" style={{ width: '100%' }}
            value={form.sezione} onChange={e => set('sezione', e.target.value)} placeholder="es. I, II..." />
        </LabelInput>
        <LabelInput label="N. R.G.">
          <input className="input-filtro" style={{ width: '100%' }}
            value={form.numero_rg}
            onChange={e => set('numero_rg', e.target.value)}
            placeholder="es. 1234/24" />
        </LabelInput>
        <LabelInput label="Contributo unificato (€)">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ flex: 1 }}
              value={form.contributo_unificato}
              onChange={e => set('contributo_unificato', e.target.value)}
              disabled={form.cu_esente} placeholder="0.00" />
            <label style={{ fontSize: 12, color: '#718096', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.cu_esente}
                onChange={e => set('cu_esente', e.target.checked)} style={{ marginRight: 4 }} />
              Esente
            </label>
          </div>
        </LabelInput>
        <LabelInput label="Pagato F24">
          <select className="input-filtro" style={{ width: '100%',
            background: form.cu_esente ? '#F7F8FA' : form.cu_f24_pagato ? '#E2EFDA' : '#FED7D7',
            border: form.cu_esente ? '' : form.cu_f24_pagato ? '1px solid #C6E0B4' : '1px solid #FEB2B2' }}
            value={form.cu_f24_pagato} disabled={form.cu_esente}
            onChange={e => set('cu_f24_pagato', e.target.value === 'true')}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
      </div>
      <LabelInput label="Note">
        <textarea className="input-filtro" style={{ width: '100%', height: 60, resize: 'vertical', background: '#FFFBF0', border: '1px solid #F6E05E' }}
          value={form.note} onChange={e => set('note', e.target.value)}
          placeholder="Note aggiuntive..." />
      </LabelInput>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn-cerca" onClick={salva} disabled={carico}>
          {carico ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
          Salva ricorso
        </button>
        <button onClick={onCancel} style={{
          padding: '10px 20px', borderRadius: 8, border: '1px solid #dde1e7',
          background: 'white', cursor: 'pointer', fontSize: 14, color: '#718096'
        }}>Annulla</button>
      </div>
    </div>
  );
}




// ── Sezione Rinvii Udienza ────────────────────────────────────────────────────

function SezioneRinvii({ ricorso, onUpdate }) {
  const [udienze, setUdienze] = useState([]);
  const [mostraForm, setMostraForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [carico, setCarico] = useState(false);
  const vuoto = () => ({
    data_rinvio: '', tipo_udienza: '', data_notifica_udienza: '', notificata: false,
    calendar: '',
    comunicata: false, sezione: ricorso.sezione || '', numero_rg: ricorso.numero_rg || '',
    tribunale_id: ricorso.tribunale_id || null, is_rinvio: true
  });
  const [form, setForm] = useState(vuoto());
  const [trib, setTrib] = useState(ricorso.tribunale_id ? { id: ricorso.tribunale_id, nome: ricorso.tribunale || '' } : null);
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const carica = () => {
    API.get(`/finanziario/rinvii/${ricorso.id}`)
      .then(r => setUdienze([...r.data].sort((a, b) => {
        if (a.is_rinvio !== b.is_rinvio) return a.is_rinvio ? 1 : -1;
        return (a.data_rinvio || '').localeCompare(b.data_rinvio || '');
      })))
      .catch(() => {});
  };
  useEffect(() => { carica(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ricorso.id]);
  const apriNuova = (isRinvio) => {
    setForm({ ...vuoto(), is_rinvio: isRinvio });
    setTrib(ricorso.tribunale_id ? { id: ricorso.tribunale_id, nome: ricorso.tribunale || '' } : null);
    setEditId(null); setMostraForm(true);
  };
  const apriModifica = (u) => {
    setForm({
      data_rinvio: u.data_rinvio || '', tipo_udienza: u.tipo_udienza || '',
      data_notifica_udienza: u.data_notifica_udienza || '', notificata: u.notificata || false,
      calendar: u.calendar || '',
      comunicata: u.comunicata || false, sezione: u.sezione || '', numero_rg: u.numero_rg || '',
      tribunale_id: u.tribunale_id || null, is_rinvio: u.is_rinvio
    });
    setTrib(u.tribunale_id ? { id: u.tribunale_id, nome: u.tribunale || '' } : null);
    setEditId(u.id); setMostraForm(true);
  };
  const salva = async () => {
    if (!form.data_rinvio) return;
    setCarico(true);
    const payload = {
      ...form,
      tribunale_id: trib?.id || null,
      data_notifica_udienza: form.data_notifica_udienza || null,
      tipo_udienza: form.tipo_udienza || null,
      calendar: form.calendar || null,
      sezione: form.sezione || null,
      numero_rg: form.numero_rg || null
    };
    try {
      if (editId) {
        await API.patch(`/finanziario/rinvio/${editId}`, payload);
      } else {
        await API.post('/finanziario/rinvio', { ...payload, gestione_ricorso_id: ricorso.id });
      }
      setMostraForm(false); setEditId(null); setForm(vuoto());
      carica();
    } catch(e) {
      console.error(e);
    } finally {
      setCarico(false);
    }
  };
  const elimina = async (u) => {
    if (!window.confirm(`Eliminare ${u.is_rinvio ? 'il rinvio' : "l'udienza"} del ${fmtDate(u.data_rinvio)}?`)) return;
    await API.delete(`/finanziario/rinvio/${u.id}`);
    carica();
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
  const Voce = ({ label, value }) => (value === null || value === undefined || value === '' || value === '—') ? null : (
    <div>
      <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#1A1A2E' }}>{value}</div>
    </div>
  );
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#E67E22', textTransform: 'uppercase' }}>
          Udienze e rinvii
        </div>
        {!mostraForm && (
          <div style={{ display: 'flex', gap: 8 }}>
            {!udienze.some(u => !u.is_rinvio) && (
              <button onClick={() => apriNuova(false)}
                style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: '1px dashed #2E75B6', background: 'white', color: '#2E75B6' }}>📅 Fissa udienza</button>
            )}
            <button onClick={() => apriNuova(true)}
              style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                border: '1px dashed #E65100', background: 'white', color: '#E65100' }}>↩ Aggiungi rinvio</button>
          </div>
        )}
      </div>
      {udienze.length === 0 && !mostraForm && (
        <div style={{ fontSize: 12, color: '#a0aec0' }}>Nessuna udienza registrata</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {udienze.map(u => (
          <div key={u.id} style={{
            background: u.is_rinvio ? '#FFF8F0' : '#F5FAFF',
            border: u.is_rinvio ? '1px solid #FFB74D' : '1px solid #BEE3F8',
            borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                background: u.is_rinvio ? '#E65100' : '#2E75B6', color: 'white', textTransform: 'uppercase' }}>
                {u.is_rinvio ? '↩ Rinvio' : '📅 Udienza'}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E' }}>{fmtDate(u.data_rinvio)}</span>
              {u.tipo_udienza && <span style={{ fontSize: 12, color: '#718096' }}>{u.tipo_udienza}</span>}
              <span title={u.is_rinvio ? (u.notificata ? 'Rinvio notificato' : 'Rinvio non notificato') : (u.notificata ? 'Udienza notificata' : 'Udienza non notificata')}
                style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: u.notificata ? '#EBF9F1' : '#FFF5F5', color: u.notificata ? '#276749' : '#C53030',
                  border: `1px solid ${u.notificata ? '#9AE6B4' : '#FEB2B2'}` }}>
                {u.is_rinvio ? 'Notificato' : 'Notificata'}: {u.notificata ? 'Sì' : 'No'}{u.notificata && u.data_notifica_udienza ? ` (${fmtDate(u.data_notifica_udienza)})` : ''}
              </span>
              <span style={{ flex: 1 }} />
              <button onClick={() => apriModifica(u)} title="Modifica"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.7 }}>✎</button>
              <button onClick={() => elimina(u)} title="Elimina"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C53030',
                  fontSize: 15, lineHeight: 1, opacity: 0.6 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
              <Voce label="Tribunale" value={u.tribunale} />
              <Voce label="Sezione" value={u.sezione} />
              <Voce label="N. R.G." value={u.numero_rg} />
              <Voce label="Data notifica udienza" value={u.data_notifica_udienza ? fmtDate(u.data_notifica_udienza) : null} />
              <Voce label="Calendar" value={u.calendar} />
              <Voce label="Comunicata al ricorrente" value={u.comunicata ? '✅ Sì' : '❌ No'} />
              {u.note && <Voce label="Note" value={u.note} />}
            </div>
          </div>
        ))}
      </div>
      {mostraForm && (
        <div style={{ background: 'white', border: '1px solid #dde1e7', borderRadius: 8,
          padding: 14, marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 10 }}>
            {editId ? 'Modifica' : form.is_rinvio ? 'Nuovo' : 'Nuova'} {form.is_rinvio ? 'rinvio' : 'udienza'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>Tribunale</div>
              <AutocompleteTribunali value={trib} onChange={t => { setTrib(t); setF('tribunale_id', t?.id || null); }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>Sezione</div>
              <input className="input-filtro" style={{ width: '100%' }}
                value={form.sezione} onChange={e => setF('sezione', e.target.value)} placeholder="es. I, II..." />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>N. R.G.</div>
              <input className="input-filtro" style={{ width: '100%' }}
                value={form.numero_rg} onChange={e => setF('numero_rg', e.target.value)} placeholder="es. 1234/24" />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>Data udienza *</div>
              <input type="date" className="input-filtro" style={{ width: '100%' }}
                value={form.data_rinvio} onChange={e => setF('data_rinvio', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>Tipo udienza</div>
              <select className="input-filtro" style={{ width: '100%' }}
                value={form.tipo_udienza} onChange={e => setF('tipo_udienza', e.target.value)}>
                <option value="">Seleziona...</option>
                <option value="In presenza">In presenza</option>
                <option value="Trattazione scritta">Trattazione scritta</option>
                <option value="Da remoto">Da remoto</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>{form.is_rinvio ? 'Notificato' : 'Notificata'}</div>
              <select className="input-filtro" style={{ width: '100%' }}
                value={form.notificata ? 'si' : 'no'} onChange={e => setF('notificata', e.target.value === 'si')}>
                <option value="no">❌ No</option>
                <option value="si">✅ Sì</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>Note</div>
              <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }}
                value={form.calendar} onChange={e => setF('calendar', e.target.value)} placeholder="Note sull'udienza..." />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#718096', textTransform: 'uppercase', marginBottom: 3 }}>Comunicata al ricorrente</div>
              <select className="input-filtro" style={{ width: '100%' }}
                value={form.comunicata ? 'si' : 'no'} onChange={e => setF('comunicata', e.target.value === 'si')}>
                <option value="no">❌ No</option>
                <option value="si">✅ Sì</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={salva} disabled={carico || !form.data_rinvio}
              style={{ padding: '6px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: '1px solid #276749', background: '#E2EFDA', color: '#276749', fontWeight: 600 }}>
              {editId ? 'Salva modifiche' : 'Salva'}</button>
            <button onClick={() => { setMostraForm(false); setEditId(null); }}
              style={{ padding: '6px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: '1px solid #dde1e7', background: 'white', color: '#718096' }}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sezione Sentenza ──────────────────────────────────────────────────────────

// Spese liquidate -> stato ottemperanza spese (richiesta del 23/10), come
// finanziario.stato_spese_con_liquidazione che decide al salvataggio: liquidato con stato vuoto,
// Pronta o Da depositare -> Liquidato (Depositata resta); tolto liquidato, Liquidato torna vuoto.
export function statoSpeseConLiquidazione(liquidato, stato) {
  if (liquidato && ['', 'Pronta', 'Da depositare'].includes(stato || '')) return 'Liquidato';
  if (!liquidato && stato === 'Liquidato') return '';
  return stato || '';
}

function SezioneSentenza({ ricorso, onUpdate, onOttemperanzaDepositata, onProponiOttemperanza }) {
  const [mostraForm, setMostraForm] = useState(false);
  const [form, setForm] = useState({
    tipo_provvedimento: ricorso.tipo_provvedimento || 'Sentenza',
    attivazione_bonus: ricorso.attivazione_bonus || false,
    data_attivazione_bonus: ricorso.data_attivazione_bonus && ricorso.data_attivazione_bonus !== 'None' ? ricorso.data_attivazione_bonus.substring(0,10) : '',
    numero_sentenza: ricorso.numero_sentenza || '',
    data_sentenza: ricorso.data_sentenza && ricorso.data_sentenza !== 'None' ? ricorso.data_sentenza.substring(0,10) : '',
    esito: ricorso.esito || '',
    comunicata: !!ricorso.sentenza_comunicata,
    importo_spese: ricorso.importo_spese || '',
    spese_compensate: ricorso.spese_compensate || false,
    importo_riconosciuto_ricorrente: ricorso.importo_riconosciuto_ricorrente || '',
    data_notifica_sentenza: ricorso.data_notifica_sentenza && ricorso.data_notifica_sentenza !== 'None' ? ricorso.data_notifica_sentenza.substring(0,10) : '',
    spese_proforma: ricorso.spese_proforma || false,
    proforma_pronta: ricorso.proforma_pronta || false,
    proforma_inviata: ricorso.proforma_inviata || false,
    data_invio_proforma: ricorso.data_invio_proforma && ricorso.data_invio_proforma !== 'None' ? ricorso.data_invio_proforma.substring(0,10) : '',
    liquidato: ricorso.liquidato || false,
    importo_liquidato: ricorso.importo_liquidato || '',
    data_liquidazione: ricorso.data_liquidazione && ricorso.data_liquidazione !== 'None' ? ricorso.data_liquidazione.substring(0,10) : '',
    pagamento_ricorrente_data: ricorso.pagamento_ricorrente_data && ricorso.pagamento_ricorrente_data !== 'None' ? ricorso.pagamento_ricorrente_data.substring(0,10) : '',
    pagamento_ricevuto: ricorso.pagamento_ricevuto || false,
    data_riconoscimento_importo: ricorso.data_riconoscimento_importo && ricorso.data_riconoscimento_importo !== 'None' ? ricorso.data_riconoscimento_importo.substring(0,10) : '',
    data_comunicazione_importo: ricorso.data_comunicazione_importo && ricorso.data_comunicazione_importo !== 'None' ? ricorso.data_comunicazione_importo.substring(0,10) : '',
    note_pagamento_ricorrente: ricorso.note_pagamento_ricorrente || '',
    note_liquidazione_studio: ricorso.note_liquidazione_studio || '',
    note_importi: ricorso.note_importi || '',
    passaggio_in_giudicato: ricorso.passaggio_in_giudicato || false,
    data_passaggio_giudicato: ricorso.data_passaggio_giudicato && ricorso.data_passaggio_giudicato !== 'None' ? ricorso.data_passaggio_giudicato.substring(0,10) : '',
    appello_controparte: ricorso.appello_controparte || false,
    ricorso_in_appello: ricorso.ricorso_in_appello || 'No',
    scadenza_appello: ricorso.scadenza_appello && ricorso.scadenza_appello !== 'None' ? ricorso.scadenza_appello.substring(0,10) : '',
    // inserimento: "Notifica" parte da No (richiesta del 23/10); una sentenza già salvata mostra il suo valore
    notifica_no: ricorso.sentenza_id ? !!ricorso.notifica_no : true,
    motivo_no_notifica: ricorso.motivo_no_notifica || '',
    note_istanza_giudicato: ricorso.note_istanza_giudicato || '',
    ottemperanza_spese_pronta: ricorso.ottemperanza_spese_pronta || false,
    stato_ottemperanza: ricorso.stato_ottemperanza || '',
    stato_ottemperanza_spese: ricorso.stato_ottemperanza_spese || '',
    adesione_ottemperanza: ricorso.adesione_ottemperanza || false,
    pronta_ottemperanza: ricorso.pronta_ottemperanza || false,
  });
  const [carico, setCarico] = useState(false);
  const [errore, setErrore] = useState(null);
  const [tribunaleSentenza, setTribunaleSentenza] = useState(
    ricorso.tribunale_sentenza_id
      ? { id: ricorso.tribunale_sentenza_id, nome: ricorso.tribunale_sentenza || '' }
      : ricorso.tribunale_id
        ? { id: ricorso.tribunale_id, nome: ricorso.tribunale || '' }
        : null
  );

  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
  const fmt = (n) => n ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n) : '—';
  const fmtDate = (d) => d && d !== 'None' ? new Date(d).toLocaleDateString('it-IT') : '—';

  const salva = async () => {
    setCarico(true); setErrore(null);
    const cleanForm = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      await API.patch(`/finanziario/gestione-ricorso/${ricorso.id}`, {
        attivazione_bonus: form.attivazione_bonus,
        data_attivazione_bonus: form.attivazione_bonus ? cleanForm.data_attivazione_bonus : null,
      });
      if (ricorso.sentenza_id) {
        await API.patch(`/finanziario/sentenza/${ricorso.sentenza_id}`, {
          ...cleanForm,
          attivazione_bonus: undefined,
          data_attivazione_bonus: undefined,
          importo_spese: cleanForm.spese_compensate ? 0 : (cleanForm.importo_spese ? parseFloat(cleanForm.importo_spese) : null),
          importo_riconosciuto_ricorrente: cleanForm.importo_riconosciuto_ricorrente ? parseFloat(cleanForm.importo_riconosciuto_ricorrente) : null,
          proforma_inviata: form.proforma_pronta ? form.proforma_inviata : false,
          data_invio_proforma: (form.proforma_pronta && form.proforma_inviata) ? cleanForm.data_invio_proforma : null,
          data_liquidazione: form.liquidato ? cleanForm.data_liquidazione : null,
          importo_liquidato: form.liquidato && cleanForm.importo_liquidato ? parseFloat(cleanForm.importo_liquidato) : null,
          data_notifica_sentenza: form.notifica_no ? null : cleanForm.data_notifica_sentenza,
        });
      } else {
        await API.post('/finanziario/sentenza', {
          gestione_ricorso_id: ricorso.id,
          ...cleanForm,
          attivazione_bonus: undefined,
          data_attivazione_bonus: undefined,
          importo_spese: cleanForm.spese_compensate ? 0 : (cleanForm.importo_spese ? parseFloat(cleanForm.importo_spese) : null),
          importo_riconosciuto_ricorrente: cleanForm.importo_riconosciuto_ricorrente ? parseFloat(cleanForm.importo_riconosciuto_ricorrente) : null,
          proforma_inviata: form.proforma_pronta ? form.proforma_inviata : false,
          data_invio_proforma: (form.proforma_pronta && form.proforma_inviata) ? cleanForm.data_invio_proforma : null,
          data_liquidazione: form.liquidato ? cleanForm.data_liquidazione : null,
          importo_liquidato: form.liquidato && cleanForm.importo_liquidato ? parseFloat(cleanForm.importo_liquidato) : null,
          data_notifica_sentenza: form.notifica_no ? null : cleanForm.data_notifica_sentenza,
        });
      }
      if (onProponiOttemperanza) {
        try {
          const ricorsoAggiornato = {
            ...ricorso,
            adesione_ottemperanza: form.adesione_ottemperanza,
            stato_ottemperanza_spese: form.stato_ottemperanza_spese,
            ricorso_in_appello: form.ricorso_in_appello,
          };
          const coll = await API.get(`/finanziario/ricorso/${encodeURIComponent(ricorso.codice_ricorso)}/collegati`);
          const generi = generiProponibili(ricorsoAggiornato, coll.data?.figli);
          if (generi.length) onProponiOttemperanza(ricorsoAggiornato, generi);
        } catch (e) { /* segnalazione best-effort */ }
      }
      await onUpdate();
      setMostraForm(false);
    } catch(e) {
      setErrore(msgErrore(e, 'Errore nel salvataggio'));
    } finally {
      setCarico(false);
    }
  };

  if (!ricorso.numero_sentenza && !mostraForm) {
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#8E44AD', marginBottom: 10, textTransform: 'uppercase' }}>Provvedimento</div>
        {ricorso.stato === 'depositato' ? (
          <button className="btn-cerca" style={{ background: '#8E44AD' }} onClick={() => setMostraForm(true)}>
            + Inserisci sentenza
          </button>
        ) : (
          <div style={{ fontSize: 12, color: '#a0aec0', fontStyle: 'italic' }}>
            Disponibile dopo il deposito del ricorso
          </div>
        )}
      </div>
    );
  }

  if (!mostraForm) {
    const Sez = ({ titolo, colore }) => (
      <div style={{ fontSize: 11, fontWeight: 700, color: colore, textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 4,
        borderBottom: `2px solid ${colore}20`, marginTop: 12 }}>{titolo}</div>
    );

    // Contatori scadenze
    const oggi = new Date();
    const notifica = ricorso.data_notifica_sentenza && ricorso.data_notifica_sentenza !== 'None'
      ? new Date(ricorso.data_notifica_sentenza) : null;
    const isTar = giorniIstanzaGiudicato(ricorso) === 60;
    const ggIstanza = isTar ? 60 : 30;
    // scadenze salvate come sono, per ogni tipo; calcolate dalla notifica solo per la Sentenza (27/10)
    const calcola = notifica && conScadenze(ricorso.tipo_provvedimento);
    const scadIstanza = ricorso.scadenza_istanza_giudicato && ricorso.scadenza_istanza_giudicato !== 'None'
      ? new Date(ricorso.scadenza_istanza_giudicato)
      : (calcola ? new Date(notifica.getTime() + ggIstanza*86400000) : null);
    const scadOtt = ricorso.scadenza_liquidazione && ricorso.scadenza_liquidazione !== 'None'
      ? new Date(ricorso.scadenza_liquidazione)
      : (calcola ? new Date(notifica.getTime() + 120*86400000) : null);
    const giorni60 = scadIstanza ? Math.ceil((scadIstanza - oggi) / 86400000) : null;
    const giorni120 = scadOtt ? Math.ceil((scadOtt - oggi) / 86400000) : null;
    const istanzaPresentata = ricorso.data_istanza_giudicato && ricorso.data_istanza_giudicato !== 'None';
    const ottAttiva = ricorso.ottemperanza_depositata === true;
    const fmtScad = d => d ? d.toLocaleDateString('it-IT') : '—';

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8E44AD', textTransform: 'uppercase' }}>Provvedimento</div>
          <button onClick={() => setMostraForm(true)}
            style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #718096',
              background: 'white', cursor: 'pointer', fontSize: 11, color: '#718096' }}>
            ✏️ Modifica sentenza
          </button>
        </div>

        <Sez titolo={ricorso.tipo_provvedimento || 'Sentenza'} colore="#8E44AD" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          <Campo label="Tipo provvedimento" value={ricorso.tipo_provvedimento || 'Sentenza'} />
          <Campo label="Tribunale" value={ricorso.tribunale} />
          <Campo label="Nr. provvedimento" value={ricorso.numero_sentenza} />
          <Campo label="Data pubblicazione" value={fmtDate(ricorso.data_sentenza)} />
          <div>
            <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Esito</div>
            <div style={{ fontSize: 13, fontWeight: 500, padding: '5px 8px', borderRadius: 5, border: '1px solid #edf2f7',
              background: ricorso.esito === 'accolto' ? '#E2EFDA' : ricorso.esito === 'rigettato' ? '#FCECEA' : ricorso.esito === 'parziale' ? '#F2F2F2' : '#F7F8FA',
              color: ricorso.esito === 'accolto' ? '#276749' : ricorso.esito === 'rigettato' ? '#C53030' : ricorso.esito === 'parziale' ? '#4A5568' : '#718096'
            }}>{ricorso.esito || '—'}</div>
          </div>
          <Campo label="Comunicata al cliente" value={ricorso.sentenza_comunicata ? '✓ Sì' : '✕ No'} highlight={ricorso.sentenza_comunicata} />
          <Campo label="Note" value={ricorso.note_importi} nota />
        </div>

        <Sez titolo="Importi studio" colore="#276749" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          <Campo label="Importo spese studio" value={ricorso.spese_compensate ? '0 — COMPENSATO' : fmt(ricorso.importo_spese)} highlight={!ricorso.liquidato && ricorso.importo_spese && !ricorso.spese_compensate} />
          {ricorso.spese_compensate && <Campo label="Compensa" value="✅ Sì" />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 10 }}>
          <Campo label="Proforma pronta" value={ricorso.proforma_pronta ? 'Sì' : 'No'} />
          {ricorso.proforma_pronta && <Campo label="Proforma inviata" value={ricorso.proforma_inviata ? 'Sì' : 'No'} />}
          {ricorso.proforma_pronta && ricorso.proforma_inviata && <Campo label="Data invio proforma" value={fmtDate(ricorso.data_invio_proforma)} />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 10 }}>
          <Campo label="Liquidato" value={ricorso.liquidato ? 'Sì' : 'No'} />
          {ricorso.liquidato && <Campo label="Data liquidazione" value={fmtDate(ricorso.data_liquidazione)} />}
          {ricorso.liquidato && <Campo label="Importo liquidato" value={fmt(ricorso.importo_liquidato)} />}
        </div>
        {ricorso.note_liquidazione_studio && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Note liquidazione studio</div>
            <div style={{ fontSize: 13, color: '#1A1A2E', padding: '6px 10px', background: '#FFFBF0',
              borderRadius: 5, border: '1px solid #F6E05E' }}>{ricorso.note_liquidazione_studio}</div>
          </div>
        )}
        <Sez titolo="Pagamento ricorrente" colore="#E67E22" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Importo riconosciuto al ricorrente</div>
            <div style={{ fontSize: 13, fontWeight: 500, padding: '5px 8px', borderRadius: 5,
              background: ricorso.attivazione_bonus ? '#E2EFDA' : '#FCECEA',
              border: ricorso.attivazione_bonus ? '1px solid #C6E0B4' : '1px solid #FEB2B2',
              color: ricorso.attivazione_bonus ? '#276749' : '#C53030' }}>
              {fmt(ricorso.importo_riconosciuto_ricorrente)}
            </div>
          </div>
          <Campo label="Attivazione bonus" value={ricorso.attivazione_bonus ? 'Sì' : 'No'} />
          <div />
          <Campo label="Data riconoscimento importo" value={fmtDate(ricorso.data_riconoscimento_importo)} />
          {ricorso.attivazione_bonus && <Campo label="Data attivazione bonus" value={fmtDate(ricorso.data_attivazione_bonus)} />}
          <Campo label="Data comunicazione" value={fmtDate(ricorso.data_comunicazione_importo)} />
          {ricorso.note_pagamento_ricorrente && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Note pagamento ricorrente</div>
              <div style={{ fontSize: 13, color: '#1A1A2E', padding: '6px 10px', background: '#FFFBF0',
                borderRadius: 5, border: '1px solid #F6E05E' }}>{ricorso.note_pagamento_ricorrente}</div>
            </div>
          )}
        </div>
        <Sez titolo="Notifica e scadenze" colore="#2E75B6" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <Campo label="Notifica" value={ricorso.notifica_no ? 'No' : 'Sì'} />
          {ricorso.notifica_no && <Campo label="Motivo" value={ricorso.motivo_no_notifica} />}
          {!ricorso.notifica_no && <Campo label="Data notifica provvedimento" value={fmtDate(ricorso.data_notifica_sentenza)} />}
          <div style={{ gridColumn: '1' }}>
            <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', marginBottom: 2 }}>
              Scad. istanza giudicato (+{ggIstanza}gg)
            </div>
            {istanzaPresentata || (giorni60 !== null && giorni60 < 0) ? (
              <div style={{ padding: '5px 8px', borderRadius: 5, fontSize: 13,
                background: '#E2EFDA', color: '#276749', border: '1px solid #C6E0B4', fontWeight: 500 }}>
                Termine maturato in data {fmtScad(scadIstanza)}
                {istanzaPresentata && <span style={{ marginLeft: 8, fontSize: 11 }}>
                  (istanza presentata il {fmtDate(ricorso.data_istanza_giudicato)})</span>}
              </div>
            ) : (
              <div style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid #edf2f7', fontSize: 13,
                background: giorni60 === null ? '#F7F8FA' : giorni60 < 10 ? '#FAEEDA' : '#E2EFDA',
                color: giorni60 === null ? '#718096' : giorni60 < 10 ? '#633806' : '#276749',
                fontWeight: giorni60 !== null && giorni60 < 10 ? 500 : 400 }}>
                {fmtScad(scadIstanza)}
                {giorni60 !== null && <span style={{ marginLeft: 8, fontSize: 11 }}>({giorni60}gg rimasti)</span>}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', marginBottom: 2 }}>
              Scadenza ottemperanza (+120gg)
            </div>
            {ottAttiva || (giorni120 !== null && giorni120 < 0) ? (
              <div style={{ padding: '5px 8px', borderRadius: 5, fontSize: 13,
                background: '#EBF4FF', color: '#2E75B6', border: '1px solid #BEE3F8', fontWeight: 500 }}>
                Termine maturato in data {fmtScad(scadOtt)}
                {ottAttiva && <span style={{ marginLeft: 8, fontSize: 11 }}>(ottemperanza depositata)</span>}
              </div>
            ) : (
              <div style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid #edf2f7', fontSize: 13,
                background: giorni120 === null ? '#F7F8FA' : giorni120 < 15 ? '#FAEEDA' : '#EBF4FF',
                color: giorni120 === null ? '#718096' : giorni120 < 15 ? '#633806' : '#2E75B6',
                fontWeight: giorni120 !== null && giorni120 < 15 ? 500 : 400 }}>
                {fmtScad(scadOtt)}
                {giorni120 !== null && <span style={{ marginLeft: 8, fontSize: 11 }}>({giorni120}gg rimasti)</span>}
              </div>
            )}
          </div>
        </div>



      <Sez titolo="Giudicato e ottemperanza" colore="#C0392B" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          <Campo label="Data presentazione istanza" value={fmtDate(ricorso.data_istanza_giudicato)} />
          <Campo label="Note istanza giudicato" value={ricorso.note_istanza_giudicato} nota />
          <Campo label="Passaggio giudicato" value={ricorso.passaggio_in_giudicato ? 'Sì' : 'No'} />
          <Campo label="Data passaggio giudicato" value={fmtDate(ricorso.data_passaggio_giudicato)} />
          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            <Campo label="Adesione ottemperanza" value={ricorso.adesione_ottemperanza ? 'Sì' : 'No'} />
            <Campo label="Stato ottemperanza" value={ricorso.stato_ottemperanza} highlight={ricorso.stato_ottemperanza === 'Pronta'} />
            <Campo label="Stato ottemperanza spese" value={ricorso.stato_ottemperanza_spese} highlight={ricorso.stato_ottemperanza_spese === 'Pronta'} />
          </div>
        </div>
        <Sez titolo="Varie" colore="#718096" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          <Campo label="Ricorso in appello" value={ricorso.ricorso_in_appello || 'No'} />
          <Campo label="Appello controparte" value={ricorso.appello_controparte ? 'Sì' : 'No'} />
          {ricorso.ricorso_in_appello === "Idonea all'appello" && (() => {
            const ds = ricorso.data_sentenza && ricorso.data_sentenza !== 'None' ? new Date(ricorso.data_sentenza) : null;
            const scad = ricorso.scadenza_appello && ricorso.scadenza_appello !== 'None'
              ? new Date(ricorso.scadenza_appello)
              : (ds ? new Date(ds.getTime() + 90*86400000) : null);
            if (!scad) return null;
            const oggi = new Date();
            const gg = Math.ceil((scad - oggi) / 86400000);
            const scadFmt = scad.toLocaleDateString('it-IT');
            return (
              <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                background: gg <= 0 ? '#E2EFDA' : gg <= 10 ? '#FED7D7' : gg <= 80 ? '#FAEEDA' : '#EBF4FF',
                color: gg <= 0 ? '#276749' : gg <= 10 ? '#C53030' : gg <= 80 ? '#633806' : '#2E75B6',
                border: '1px solid #e8eaed' }}>
                {gg <= 0
                  ? `Termine appello decorso il ${scadFmt}`
                  : `Scadenza appello: ${scadFmt} — ${gg} giorni rimasti`}
                {gg > 0 && gg <= 10 && ' ⚠️'}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  const Sez = ({ titolo, colore }) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: colore, textTransform: 'uppercase',
      letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6,
      borderBottom: `2px solid ${colore}20`, marginTop: 16 }}>{titolo}</div>
  );

  return (
    <div style={{ marginTop: 16, background: '#F8F0FF', border: '1px solid #D6A8FF', borderRadius: 10, padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#8E44AD', marginBottom: 16 }}>
        {ricorso.sentenza_id ? 'Modifica sentenza' : 'Nuova sentenza'}
      </div>
      {errore && <div className="alert errore" style={{ marginBottom: 12 }}><span>{errore}</span></div>}

      <Sez titolo={form.tipo_provvedimento || 'Sentenza'} colore="#8E44AD" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Tipo provvedimento">
          <select className="input-filtro" style={{ width: '100%' }} value={form.tipo_provvedimento} aria-label="Tipo provvedimento"
            onChange={e => set('tipo_provvedimento', e.target.value)}>
            {tipiSelezionabili(ricorso.tipo_provvedimento).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </LabelInput>
        <LabelInput label="Tribunale">
          <AutocompleteTribunali value={tribunaleSentenza}
            onChange={t => { setTribunaleSentenza(t); set('tribunale_sentenza_id', t?.id || null); }} />
        </LabelInput>
        <LabelInput label="Nr. provvedimento">
          <input className="input-filtro" style={{ width: '100%' }} value={form.numero_sentenza}
            onChange={e => set('numero_sentenza', e.target.value)} placeholder="es. 16/25" />
        </LabelInput>
        <LabelInput label="Data pubblicazione">
          <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_sentenza}
            onChange={e => set('data_sentenza', e.target.value)} />
        </LabelInput>
        <LabelInput label="Esito">
          <select className="input-filtro" style={{ width: '100%',
            background: form.esito === 'accolto' ? '#E2EFDA' : form.esito === 'rigettato' ? '#FCECEA' : form.esito === 'parziale' ? '#F2F2F2' : ['inammissibile','cessata_materia','improcedibile','incompetenza','nomina_commissario','estinto'].includes(form.esito) ? '#FEF3C7' : 'white',
            color: form.esito === 'accolto' ? '#276749' : form.esito === 'rigettato' ? '#C53030' : form.esito === 'parziale' ? '#4A5568' : ['inammissibile','cessata_materia','improcedibile','incompetenza','nomina_commissario','estinto'].includes(form.esito) ? '#92400E' : '#1A1A2E',
            fontWeight: form.esito ? 500 : 400
          }} value={form.esito} onChange={e => set('esito', e.target.value)}>
            <option value="">Seleziona...</option>
            <option value="accolto">Accolto</option>
            <option value="rigettato">Rigettato</option>
            <option value="parziale">Parziale</option>
            <option value="inammissibile">Inammissibile</option>
            <option value="cessata_materia">Cessata materia</option>
            <option value="improcedibile">Improcedibile</option>
            <option value="incompetenza">Incompetenza</option>
            <option value="nomina_commissario">Nomina commissario</option>
            <option value="estinto">Estinto</option>
          </select>
        </LabelInput>
        <LabelInput label="Comunicata al cliente">
          <select className="input-filtro" style={{ width: '100%', background: form.comunicata ? '#E2EFDA' : '#F2F2F2', color: form.comunicata ? '#276749' : '#4A5568', fontWeight: 500 }}
            value={form.comunicata ? 'si' : 'no'} onChange={e => set('comunicata', e.target.value === 'si')}>
            <option value="no">✕ No</option>
            <option value="si">✓ Sì</option>
          </select>
        </LabelInput>
        <LabelInput label="Note">
          <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }} value={form.note_importi}
            onChange={e => set('note_importi', e.target.value)} placeholder="es. Riunita con..." />
        </LabelInput>
      </div>

      <Sez titolo="Importi studio" colore="#276749" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Importo spese studio (€)">
          {form.spese_compensate ? (
            <input type="text" className="input-filtro" readOnly
              style={{ width: '100%', background: '#FAEEDA', color: '#633806', fontWeight: 600 }}
              value="0 — COMPENSATO" />
          ) : (
            <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: '100%' }} value={form.importo_spese}
              onChange={e => set('importo_spese', e.target.value)} placeholder="0.00" />
          )}
        </LabelInput>
        <LabelInput label="Compensa">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34 }}>
            <input type="checkbox" checked={form.spese_compensate || false}
              onChange={e => set('spese_compensate', e.target.checked)} />
            <span style={{ fontSize: 12, color: '#4a5568' }}>Spese compensate</span>
          </div>
        </LabelInput>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Proforma pronta">
          <select className="input-filtro" style={{ width: '100%' }} value={form.proforma_pronta}
            onChange={e => {
              const attivo = e.target.value === 'true';
              set('proforma_pronta', attivo);
              if (!attivo) { set('proforma_inviata', false); set('data_invio_proforma', ''); }
            }}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
        {form.proforma_pronta && (
        <LabelInput label="Proforma inviata">
          <select className="input-filtro" style={{ width: '100%' }} value={form.proforma_inviata}
            onChange={e => {
              const attivo = e.target.value === 'true';
              set('proforma_inviata', attivo);
              if (!attivo) set('data_invio_proforma', '');
            }}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
        )}
        {form.proforma_inviata && (
          <LabelInput label="Data invio proforma">
            <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_invio_proforma}
              onChange={e => set('data_invio_proforma', e.target.value)} />
          </LabelInput>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Liquidato">
          <select className="input-filtro" style={{ width: '100%' }} value={form.liquidato} aria-label="Liquidato"
            onChange={e => {
              const attivo = e.target.value === 'true';
              set('liquidato', attivo);
              if (!attivo) { set('data_liquidazione', ''); set('importo_liquidato', ''); }
              set('stato_ottemperanza_spese', statoSpeseConLiquidazione(attivo, form.stato_ottemperanza_spese));
            }}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
        {form.liquidato && (
          <LabelInput label="Data liquidazione">
            <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_liquidazione}
              onChange={e => set('data_liquidazione', e.target.value)} />
          </LabelInput>
        )}
        {form.liquidato && (
          <LabelInput label="Importo liquidato (€)">
            <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: '100%' }} value={form.importo_liquidato}
              onChange={e => set('importo_liquidato', e.target.value)} placeholder="0.00" />
          </LabelInput>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Note liquidazione studio">
          <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }} value={form.note_liquidazione_studio}
            onChange={e => set('note_liquidazione_studio', e.target.value)} placeholder="Note..." />
        </LabelInput>
      </div>
      <Sez titolo="Pagamento ricorrente" colore="#E67E22" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Importo riconosciuto al ricorrente (€)">
          <input type="number" min="0" onWheel={senzaRotella} className="input-filtro"
            style={{ width: '100%',
              background: form.attivazione_bonus ? '#E2EFDA' : '#FCECEA',
              border: form.attivazione_bonus ? '1px solid #C6E0B4' : '1px solid #FEB2B2' }}
            value={form.importo_riconosciuto_ricorrente}
            onChange={e => set('importo_riconosciuto_ricorrente', e.target.value)} placeholder="0.00" />
        </LabelInput>
        <LabelInput label="Attivazione bonus">
          <select className="input-filtro" style={{ width: '100%' }} value={form.attivazione_bonus}
            onChange={e => {
              const attivo = e.target.value === 'true';
              set('attivazione_bonus', attivo);
              if (!attivo) set('data_attivazione_bonus', '');
            }}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
        <div />
        <LabelInput label="Data riconoscimento importo">
          <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_riconoscimento_importo}
            onChange={e => set('data_riconoscimento_importo', e.target.value)} />
        </LabelInput>
        {form.attivazione_bonus && (
          <LabelInput label="Data attivazione bonus">
            <input type="date" className="input-filtro" style={{ width: '100%' }}
              value={form.data_attivazione_bonus} onChange={e => set('data_attivazione_bonus', e.target.value)} />
          </LabelInput>
        )}
        <LabelInput label="Data comunicazione">
          <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_comunicazione_importo}
            onChange={e => set('data_comunicazione_importo', e.target.value)} />
        </LabelInput>
        <LabelInput label="Note pagamento ricorrente">
          <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }} value={form.note_pagamento_ricorrente}
            onChange={e => set('note_pagamento_ricorrente', e.target.value)} placeholder="Note..." />
        </LabelInput>
      </div>
      <Sez titolo="Notifica e scadenze" colore="#2E75B6" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Notifica">
          <select className="input-filtro" style={{ width: '100%' }} value={form.notifica_no}
            onChange={e => {
              const no = e.target.value === 'true';
              set('notifica_no', no);
              if (no) set('data_notifica_sentenza', '');
            }}>
            <option value="false">Sì</option>
            <option value="true">No</option>
          </select>
        </LabelInput>
        {form.notifica_no && (
          <LabelInput label="Motivo">
            <select className="input-filtro" style={{ width: '100%' }} value={form.motivo_no_notifica}
              onChange={e => set('motivo_no_notifica', e.target.value)}>
              <option value="">—</option>
              <option value="Appello">Appello</option>
              <option value="Istanza di correzione">Istanza di correzione</option>
            </select>
          </LabelInput>
        )}
        {!form.notifica_no && (
          <LabelInput label="Data notifica provvedimento">
            <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_notifica_sentenza}
              onChange={e => set('data_notifica_sentenza', e.target.value)} />
          </LabelInput>
        )}
        {!form.notifica_no && <div />}
        {form.notifica_no && <div />}
        <LabelInput label={`Scadenza istanza giudicato (+${giorniIstanzaGiudicato(ricorso, tribunaleSentenza?.nome)}gg)`}>
          <div style={{ padding: '6px 10px', background: '#EBF4FF', borderRadius: 6,
            border: '1px solid #BEE3F8', fontSize: 13, color: '#2E75B6' }}>
            {!conScadenze(form.tipo_provvedimento) ? '— solo per le sentenze' : form.data_notifica_sentenza ?
              new Date(new Date(form.data_notifica_sentenza).getTime() + giorniIstanzaGiudicato(ricorso, tribunaleSentenza?.nome)*86400000).toLocaleDateString('it-IT')
              : '— inserire data notifica'}
          </div>
        </LabelInput>
        <LabelInput label="Scadenza liquidazione (+120gg)">
          <div style={{ padding: '6px 10px', background: '#FFF5F5', borderRadius: 6,
            border: '1px solid #FEB2B2', fontSize: 13, color: '#c53030' }}>
            {!conScadenze(form.tipo_provvedimento) ? '— solo per le sentenze' : form.data_notifica_sentenza ?
              new Date(new Date(form.data_notifica_sentenza).getTime() + 120*86400000).toLocaleDateString('it-IT')
              : '— inserire data notifica'}
          </div>
        </LabelInput>
      </div>


      <Sez titolo="Giudicato e ottemperanza" colore="#C0392B" />

      {form.data_notifica_sentenza && conScadenze(form.tipo_provvedimento) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {(() => {
            const notifica = new Date(form.data_notifica_sentenza);
            const oggi = new Date();
            const isTar = giorniIstanzaGiudicato(ricorso, tribunaleSentenza?.nome) === 60;
            const ggI = isTar ? 60 : 30;
            const scad60 = new Date(notifica.getTime() + ggI*86400000);
            const scad120 = new Date(notifica.getTime() + 120*86400000);
            const giorni60 = Math.ceil((scad60 - oggi) / 86400000);
            const giorni120 = Math.ceil((scad120 - oggi) / 86400000);
            const fmtS = d => d.toLocaleDateString('it-IT');
            return (
              <>
                <div style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                  background: giorni60 < 0 ? '#E2EFDA' : giorni60 < 10 ? '#FAEEDA' : '#E2EFDA',
                  color: giorni60 < 0 ? '#276749' : giorni60 < 10 ? '#633806' : '#276749',
                  border: '1px solid #ccc' }}>
                  Istanza giudicato (+{ggI}gg): {giorni60 < 0 ? 'termine maturato il ' + fmtS(scad60) : giorni60 + ' giorni rimasti'}
                </div>
                <div style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                  background: giorni120 < 0 ? '#EBF4FF' : giorni120 < 15 ? '#FAEEDA' : '#EBF4FF',
                  color: giorni120 < 0 ? '#2E75B6' : giorni120 < 15 ? '#633806' : '#2E75B6',
                  border: '1px solid #ccc' }}>
                  Scadenza ottemperanza: {giorni120 < 0 ? 'termine maturato il ' + fmtS(scad120) : giorni120 + ' giorni rimasti'}
                </div>
              </>
            );
          })()}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Stato giudicato">
          <select className="input-filtro" style={{ width: '100%' }}
            value={form.passaggio_in_giudicato === true || form.passaggio_in_giudicato === 'true' ? 'ottenuto' : form.data_istanza_giudicato ? 'istanza_presentata' : 'in_attesa'}
            onChange={e => { if (e.target.value === 'ottenuto') set('passaggio_in_giudicato', true); else set('passaggio_in_giudicato', false); }}>
            <option value="in_attesa">In attesa</option>
            <option value="istanza_presentata">Istanza presentata</option>
            <option value="ottenuto">Ottenuto</option>
          </select>
        </LabelInput>
        <LabelInput label="Data presentazione istanza">
          <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_istanza_giudicato}
            onChange={e => set('data_istanza_giudicato', e.target.value)} />
        </LabelInput>
        <LabelInput label="Data passaggio giudicato">
          <input type="date" className="input-filtro" style={{ width: '100%' }} value={form.data_passaggio_giudicato}
            onChange={e => set('data_passaggio_giudicato', e.target.value)} />
        </LabelInput>
        <div style={{ gridColumn: '1 / -1' }}>
        <LabelInput label="Note istanza giudicato">
          <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }} value={form.note_istanza_giudicato}
            onChange={e => set('note_istanza_giudicato', e.target.value)} placeholder="es. sollecito, richiesta CDA..." />
        </LabelInput>
        </div>
        <LabelInput label="Adesione ottemperanza">
          <select className="input-filtro" style={{ width: '100%' }} value={form.adesione_ottemperanza}
            onChange={e => set('adesione_ottemperanza', e.target.value === 'true')}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
        <LabelInput label="Stato ottemperanza">
          <select className="input-filtro" style={{ width: '100%' }} value={form.stato_ottemperanza}
            onChange={e => set('stato_ottemperanza', e.target.value)}>
            <option value="">Seleziona...</option>
            <option value="Pronta">Pronta</option>
            <option value="Da depositare">Da depositare</option>
            <option value="Revocata">Revocata</option>
            <option value="Depositata">Depositata</option>
          </select>
        </LabelInput>
        <LabelInput label="Stato ottemperanza spese">
          <select className="input-filtro" style={{ width: '100%' }} value={form.stato_ottemperanza_spese} aria-label="Stato ottemperanza spese"
            onChange={e => set('stato_ottemperanza_spese', e.target.value)}>
            <option value="">Seleziona...</option>
            <option value="Pronta">Pronta</option>
            <option value="Da depositare">Da depositare</option>
            <option value="Depositata">Depositata</option>
            <option value="Liquidato">Liquidato</option>
          </select>
        </LabelInput>
      </div>
      <Sez titolo="Varie" colore="#718096" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 8 }}>
        <LabelInput label="Ricorso in appello">
          <select className="input-filtro" style={{ width: '100%' }} value={form.ricorso_in_appello}
            onChange={e => set('ricorso_in_appello', e.target.value)}>
            <option value="No">No</option>
            <option value="Sì">Sì</option>
            <option value="Idonea all'appello">Idonea all'appello</option>
          </select>
        </LabelInput>
        <LabelInput label="Appello controparte">
          <select className="input-filtro" style={{ width: '100%' }} value={form.appello_controparte}
            onChange={e => set('appello_controparte', e.target.value === 'true')}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-cerca" style={{ background: '#8E44AD' }} onClick={salva} disabled={carico}>
          {carico ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
          Salva sentenza
        </button>
        <button onClick={() => setMostraForm(false)} style={{
          padding: '10px 20px', borderRadius: 8, border: '1px solid #dde1e7',
          background: 'white', cursor: 'pointer', fontSize: 14, color: '#718096'
        }}>Annulla</button>
      </div>
    </div>
  );
}


// ── Vista dettaglio ricorso (sola lettura) ────────────────────────────────────

function VistaDettaglio({ ricorso, tipi, avvocati, parti, onModifica, onUpdate }) {
  const fmt = (n) => n ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n) : '—';
  const fmtDate = (d) => d && d !== 'None' ? new Date(d).toLocaleDateString('it-IT') : '—';

  const avvocatoNome = avvocati.find(a => a.id === ricorso.legale_studio_id)?.nome || '—';
  const parteNome = parti.find(p => p.id === ricorso.parte_resistente_id)?.nome || '—';
  const tipoNome = tipi.find(t => t.id === ricorso.tipo_ricorso_id)?.nome || '—';

  const Sezione = ({ titolo, colore, children }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: colore, textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 10, paddingBottom: 6,
        borderBottom: `2px solid ${colore}20` }}>{titolo}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px 20px' }}>
        {children}
      </div>
    </div>
  );

  const Campo = ({ label, value, highlight, nota }) => {
    if (value === null || value === undefined || value === '' || value === '—') return null;
    return (
    <div>
      <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: highlight ? '#c53030' : '#1A1A2E', fontWeight: highlight ? 500 : 400,
        padding: '5px 8px', background: nota && value ? '#FFFBF0' : '#F7F8FA', borderRadius: 5,
        border: nota && value ? '1px solid #F6E05E' : '1px solid #edf2f7' }}>
        {value}
      </div>
    </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        {ricorso.collettivo && ricorso.denominazione && (
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#1A1A2E' }} title="Nome del ricorso, fissato al deposito">
            {ricorso.denominazione}</div>
        )}
        <BadgePendente ricorso={ricorso} />
        <button onClick={onModifica}
          style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #718096',
            background: 'white', cursor: 'pointer', fontSize: 11, color: '#718096', whiteSpace: 'nowrap' }}>
          ✏️ Modifica ricorso
        </button>
      </div>

      <Sezione titolo="Dati ricorso" colore="#2E75B6">
        <Campo label="Codice Esolver" value={ricorso.codice_esolver} />
        <Campo label="Codice Ricorso" value={ricorso.codice_ricorso} />
        <Campo label="Grado ricorso" value={{'primo_grado':'Primo grado','appello':'Appello','ottemperanza':'Ottemperanza','ottemperanza_ottemperanza':'Ottemperanza di ottemperanza'}[ricorso.grado] || ricorso.grado} />
        <Campo label="Tipo ricorso" value={tipoNome} />
        <Campo label="Origine" value={ORIGINI[ricorso.origine] || ricorso.origine} />
        <Campo label="Ricorso collettivo" value={ricorso.collettivo ? `Sì · ${ricorso.n_aderenti || 0} aderenti` : 'No'} />
        <Campo label="Avvocato studio" value={avvocatoNome} />
        <Campo label="Data registrazione adesione" value={fmtDate(ricorso.data_registrazione)} />
        <Campo label="Stato" value={ricorso.stato?.replace(/_/g, ' ')} />
        {ricorso.stato === 'depositato' && <Campo label="Data deposito" value={fmtDate(ricorso.data_adesione)} />}
      </Sezione>

      <Sezione titolo="Parti" colore="#8E44AD">
        <Campo label={ricorso.collettivo ? 'Cognome capofila' : 'Cognome ricorrente'} value={ricorso.cognome} />
        <Campo label={ricorso.collettivo ? 'Nome capofila' : 'Nome ricorrente'} value={ricorso.nome_cliente} />
        <Campo label="Codice fiscale" value={ricorso.codice_fiscale} />
        <Campo label="Email" value={ricorso.email} />
        <Campo label="Parte resistente" value={parteNome} />
      </Sezione>

      {ricorso.collettivo && <SezioniCollettivo ricorso={ricorso} onCambio={onUpdate} />}

      {/* nei collettivi pagato e convenzione sono del singolo ricorrente (card Ricorrenti) */}
      <Sezione titolo="Compenso" colore="#276749">
        <Campo label="Compenso" value={fmt(ricorso.compenso)} />
        {ricorso.collettivo && <Campo label="Compenso convenzionati" value={fmt(ricorso.compenso_convenzionati)} />}
        {!ricorso.collettivo && <Campo label="Compenso pagato" value={ricorso.compenso_pagato ? '✅ Sì' : '❌ No'} />}
        {!ricorso.collettivo && <Campo label="Convenzione" value={ricorso.convenzione_cliente || ricorso.convenzione} />}
      </Sezione>

      <Sezione titolo="Procedura" colore="#E67E22">
        <Campo label="Tribunale" value={ricorso.tribunale} />
        <Campo label="Sezione" value={ricorso.sezione} />
        <Campo label="N. R.G." value={ricorso.numero_rg} />
        <div>
          <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Contributo unificato</div>
          <div style={{ fontSize: 13, padding: '5px 8px', borderRadius: 5,
            background: ricorso.cu_esente ? '#F7F8FA' : ricorso.cu_f24_pagato ? '#E2EFDA' : '#FED7D7',
            border: ricorso.cu_esente ? '1px solid #edf2f7' : ricorso.cu_f24_pagato ? '1px solid #C6E0B4' : '1px solid #FEB2B2',
            color: ricorso.cu_esente ? '#718096' : ricorso.cu_f24_pagato ? '#276749' : '#C53030',
            fontWeight: 500 }}>
            {ricorso.cu_esente ? 'Esente' : fmt(ricorso.contributo_unificato)}
          </div>
        </div>
        <Campo label="Pagato F24" value={ricorso.cu_esente ? '—' : ricorso.cu_f24_pagato ? 'Sì' : 'No'} />
        <Campo label="Data notifica ricorso" value={fmtDate(ricorso.data_notifica_ricorso)} />
        <Campo label="Diffida spese" value={ricorso.diffida_spese} nota />
        <Campo label="Note" value={ricorso.note_notifica} nota />
      </Sezione>

      {ricorso.note && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase',
            letterSpacing: '0.8px', marginBottom: 8 }}>Note</div>
          <div style={{ fontSize: 13, color: '#1A1A2E', padding: '8px 10px',
            background: '#FFFBF0', borderRadius: 6, border: '1px solid #F6E05E' }}>{ricorso.note}</div>
        </div>
      )}
    </div>
  );
}

// ── Form modifica ricorso ─────────────────────────────────────────────────────

function FormModificaRicorso({ ricorso, tipi, avvocati, parti, onSave, onCancel, onNuovoTipo, onElimina }) {
  const intestatarioAttuale = {
    codice_cliente: ricorso.codice_cliente, cognome: ricorso.cognome,
    nome: ricorso.nome_cliente || ricorso.nome, codice_fiscale: ricorso.codice_fiscale,
  };
  const [form, setForm] = useState({
    codice_ricorso: ricorso.codice_ricorso || '',
    codice_esolver: ricorso.codice_esolver || '',
    tipo_ricorso_id: ricorso.tipo_ricorso_id || null,
    grado: ricorso.grado || 'primo_grado',
    origine: ricorso.origine || '',
    data_registrazione: ricorso.data_registrazione && ricorso.data_registrazione !== 'None' ? ricorso.data_registrazione.substring(0,10) : '',
    data_adesione: ricorso.data_adesione && ricorso.data_adesione !== 'None' ? ricorso.data_adesione.substring(0,10) : '',
    compenso: ricorso.compenso || '',
    flag_netto: false,
    compenso_pagato: ricorso.compenso_pagato || false,
    stato: ricorso.stato || 'in_lavorazione',
    parte_resistente_id: ricorso.parte_resistente_id || null,
    legale_studio_id: ricorso.legale_studio_id || null,
    tribunale_id: ricorso.tribunale_id || null,
    numero_rg: ricorso.numero_rg || '',
    contributo_unificato: ricorso.contributo_unificato || '',
    cu_esente: ricorso.cu_esente || false,
    cu_f24_pagato: ricorso.cu_f24_pagato || false,
    tipo_udienza: ricorso.tipo_udienza || '',
    sezione: ricorso.sezione || '',
    diffida_spese: ricorso.diffida_spese || '',
    data_udienza: ricorso.data_udienza && ricorso.data_udienza !== 'None' ? ricorso.data_udienza.substring(0,10) : '',
    comunicata: ricorso.comunicata || false,
    convenzione: ricorso.convenzione || '',
    attivazione_bonus: ricorso.attivazione_bonus || false,
    data_attivazione_bonus: ricorso.data_attivazione_bonus && ricorso.data_attivazione_bonus !== 'None' ? ricorso.data_attivazione_bonus.substring(0,10) : '',
    data_notifica_ricorso: ricorso.data_notifica_ricorso && ricorso.data_notifica_ricorso !== 'None' ? ricorso.data_notifica_ricorso.substring(0,10) : '',
    note_notifica: ricorso.note_notifica || '',
    note: ricorso.note || '',
    collettivo: !!ricorso.collettivo,
    compenso_convenzionati: ricorso.compenso_convenzionati ?? ''
  });
  const [carico, setCarico] = useState(false);
  const [errore, setErrore] = useState(null);
  const [tribunale, setTribunale] = useState(
    ricorso.tribunale_id ? { id: ricorso.tribunale_id, nome: ricorso.tribunale || '' } : null
  );

  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
  const gradoTipo = campiGradoTipo(form, set, tipi, onNuovoTipo, ricorso.tipo_ricorso_id);
  const senzaNuova = tipoNonConta(tipi, form.tipo_ricorso_id);
  useEffect(() => {
    if (senzaNuova && form.origine === 'nuova_adesione') set('origine', '');
  }, [senzaNuova, form.origine, set]);

  const depositata = STATI_DEPOSITATI.includes(ricorso.stato);
  // nome proposto per il deposito di un collettivo: letto all'apertura del form, così la conferma
  // non fa chiamate (annullando non parte nulla)
  const proposta = useRef(null);
  useEffect(() => {
    if (!ricorso.collettivo || depositata) return;
    proposta.current = API.get(`/finanziario/gestione-ricorso/${ricorso.id}`)
      .then(r => r.data?.denominazione_proposta || null).catch(() => null);
  }, [ricorso.id, ricorso.collettivo, depositata]);
  const salva = async () => {
    // un'origine gia' registrata non torna vuota: se il tipo non conta piu' va scelta di nuovo
    if (ricorso.origine && !form.origine) { setErrore("Indica l'origine della scheda"); return; }
    // passaggio a depositato: definitivo, si conferma prima di qualunque chiamata
    if (!depositata && form.stato === 'depositato') {
      const testo = ricorso.collettivo ? confermaDepositoCollettivo(ricorso, await proposta.current) : CONFERMA_DEPOSITO;
      if (!window.confirm(testo)) return;
    }
    setCarico(true); setErrore(null);
    const cleanForm = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      await API.patch(`/finanziario/gestione-ricorso/${ricorso.id}`, {
        ...cleanForm,
        tribunale_id: tribunale?.id || null,
        ...cleanForm,
        compenso: cleanForm.compenso ? (cleanForm.flag_netto ? lordoDaNetto(cleanForm.compenso) : parseFloat(cleanForm.compenso)) : null,
        flag_netto: undefined,
        contributo_unificato: cleanForm.contributo_unificato ? parseFloat(cleanForm.contributo_unificato) : 0,
        ...campiCollettivo(cleanForm, ricorso),
      });
      await onSave();
    } catch(e) {
      setErrore(msgErrore(e, 'Errore nel salvataggio'));
    } finally {
      setCarico(false);
    }
  };

  return (
    <div style={{ background: '#FFF8F0', border: '1px solid #F6E05E', borderRadius: 10, padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A2E', marginBottom: 16 }}>
        ✏️ Modifica ricorso — {ricorso.codice_ricorso}
      </div>

      {errore && <div className="alert errore" style={{ marginBottom: 12 }}><span>{errore}</span></div>}

      <CasellaCollettivo form={form} set={set} />
      <IntestatarioBox cliente={intestatarioAttuale} gestioneId={ricorso.id} onCambiato={() => onSave && onSave()}
        capofila={!!ricorso.collettivo} bloccato={!!ricorso.collettivo && depositata} manuale={!!ricorso.capofila_manuale} />

      {/* DATI RICORSO */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#2E75B6', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #2E75B620' }}>
        Dati ricorso
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Codice Esolver">
          <input className="input-filtro" style={{ width: '100%' }} value={form.codice_esolver}
            onChange={e => set('codice_esolver', e.target.value)} />
        </LabelInput>
        <LabelInput label="Codice Ricorso">
          <input className="input-filtro" style={{ width: '100%' }} value={form.codice_ricorso}
            onChange={e => set('codice_ricorso', e.target.value)} />
        </LabelInput>
        <LabelInput label="Grado ricorso">{gradoTipo.grado}</LabelInput>
        <LabelInput label="Origine scheda">
          <SelectOrigine value={form.origine} onChange={v => set('origine', v)}
            vuota={ricorso.origine ? null : '— non indicata —'} senzaNuova={senzaNuova} />
          {/* schede create prima dell'obbligo: il salvataggio resta possibile, ma senza origine
              la scheda resta fuori dal report Campagne */}
          {!form.origine && (
            <div style={{ marginTop: 4, padding: '4px 8px', fontSize: 12, background: '#FFFBF0',
                          border: '1px solid #F6E05E', borderRadius: 4, color: '#744210' }}>
              Origine non indicata: sceglila, altrimenti la scheda resta fuori dal report Campagne.
            </div>
          )}
        </LabelInput>
        <LabelInput label="Tipo ricorso">{gradoTipo.tipo}</LabelInput>
        <LabelInput label="Avvocato studio">
          <SelectField value={form.legale_studio_id} onChange={v => set('legale_studio_id', v)}
            options={avvocati} placeholder="Seleziona avvocato..." />
        </LabelInput>
        <LabelInput label="Data registrazione adesione">
          <input type="date" className="input-filtro" style={{ width: '100%' }}
            value={form.data_registrazione} onChange={e => set('data_registrazione', e.target.value)} />
        </LabelInput>
        <LabelInput label="Stato">
          <select className="input-filtro" style={{ width: '100%' }} value={form.stato}
            onChange={e => {
              const s = e.target.value;
              set('stato', s);
              // la data di deposito resta negli stati successivi (chiuso, estinto…)
              if (STATI_PRIMA_DEL_DEPOSITO.includes(s)) set('data_adesione', '');
            }}>
            {/* su una scheda depositata non si torna indietro (409 dal backend) */}
            <option value="in_lavorazione" disabled={depositata}>In lavorazione</option>
            <option value="da_depositare" disabled={depositata}>Da depositare</option>
            <option value="depositato">Depositato</option>
            <option value="chiuso">Chiuso</option>
            <option value="estinto">Estinto</option>
            <option value="estinto_e_ripresentato">Estinto e ripresentato</option>
            {!form.collettivo && <option value="revocato" disabled={depositata}>Revocato</option>}
          </select>
        </LabelInput>
        {STATI_DEPOSITATI.includes(form.stato) && (
          <LabelInput label="Data deposito">
            <input type="date" className="input-filtro" style={{ width: '100%' }}
              value={form.data_adesione} onChange={e => set('data_adesione', e.target.value)} />
          </LabelInput>
        )}
      </div>

      {/* PARTI */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8E44AD', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #8E44AD20' }}>
        Parti
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Parte resistente">
          <SelectField value={form.parte_resistente_id} onChange={v => set('parte_resistente_id', v)}
            options={parti} placeholder="Seleziona..." />
        </LabelInput>
      </div>

      {/* COMPENSO */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#276749', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #27674920' }}>
        Compenso
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <LabelInput label="Compenso (€)">
          <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: '100%' }}
            value={form.compenso}
            onChange={e => set('compenso', e.target.value)} />
        </LabelInput>
        {form.collettivo && (
          <LabelInput label="Compenso convenzionati (€)">
            <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: '100%' }}
              value={form.compenso_convenzionati}
              onChange={e => set('compenso_convenzionati', e.target.value)} />
          </LabelInput>
        )}
        <LabelInput label="Cifra al netto">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34 }}>
            <input type="checkbox" checked={form.flag_netto || false}
              onChange={e => set('flag_netto', e.target.checked)} />
            <span style={{ fontSize: 12, color: '#4a5568' }}>
              {form.flag_netto && form.compenso
                ? `Lordo: ${lordoDaNetto(form.compenso).toFixed(2)} €`
                  + (form.collettivo && form.compenso_convenzionati ? ` / ${lordoDaNetto(form.compenso_convenzionati).toFixed(2)} €` : '')
                : 'Spunta se la cifra è netta'}
            </span>
          </div>
        </LabelInput>
        {!form.collettivo && <LabelInput label="Compenso pagato">
          <select className="input-filtro" style={{ width: '100%' }}
            value={form.compenso_pagato ? 'si' : 'no'}
            onChange={e => set('compenso_pagato', e.target.value === 'si')}>
            <option value="no">❌ No</option>
            <option value="si">✅ Sì</option>
          </select>
        </LabelInput>}
      </div>

      {/* PROCEDURA */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#E67E22', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #E67E2220' }}>
        Procedura
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 }}>
        <LabelInput label="Tribunale">
          <AutocompleteTribunali value={tribunale}
            onChange={t => { setTribunale(t); set('tribunale_id', t?.id || null); }} />
        </LabelInput>
        <LabelInput label="Sezione">
          <input className="input-filtro" style={{ width: '100%' }}
            value={form.sezione} onChange={e => set('sezione', e.target.value)} placeholder="es. I, II..." />
        </LabelInput>
        <LabelInput label="N. R.G.">
          <input className="input-filtro" style={{ width: '100%' }}
            value={form.numero_rg} onChange={e => set('numero_rg', e.target.value)} />
        </LabelInput>
        <LabelInput label="Contributo unificato (€)">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="number" min="0" onWheel={senzaRotella} className="input-filtro" style={{ flex: 1 }}
              value={form.contributo_unificato} onChange={e => set('contributo_unificato', e.target.value)}
              disabled={form.cu_esente} />
            <label style={{ fontSize: 12, color: '#718096', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.cu_esente}
                onChange={e => set('cu_esente', e.target.checked)} style={{ marginRight: 4 }} />
              Esente
            </label>
          </div>
        </LabelInput>
        <LabelInput label="Pagato F24">
          <select className="input-filtro" style={{ width: '100%',
            background: form.cu_esente ? '#F7F8FA' : form.cu_f24_pagato ? '#E2EFDA' : '#FED7D7',
            border: form.cu_esente ? '' : form.cu_f24_pagato ? '1px solid #C6E0B4' : '1px solid #FEB2B2' }}
            value={form.cu_f24_pagato} disabled={form.cu_esente}
            onChange={e => set('cu_f24_pagato', e.target.value === 'true')}>
            <option value="false">No</option>
            <option value="true">Sì</option>
          </select>
        </LabelInput>
        <LabelInput label="Data notifica ricorso">
          <input type="date" className="input-filtro" style={{ width: '100%' }}
            value={form.data_notifica_ricorso} onChange={e => set('data_notifica_ricorso', e.target.value)} />
        </LabelInput>
        <LabelInput label="Diffida spese">
          <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }} value={form.diffida_spese}
            onChange={e => set('diffida_spese', e.target.value)} placeholder="Data o nota diffida..." />
        </LabelInput>
        <LabelInput label="Note">
          <input className="input-filtro" style={{ width: '100%', background: '#FFFBF0', border: '1px solid #F6E05E' }} value={form.note_notifica}
            onChange={e => set('note_notifica', e.target.value)} placeholder="es. NO CALENDAR..." />
        </LabelInput>
      </div>

      <LabelInput label="Note">
        <textarea className="input-filtro" style={{ width: '100%', height: 60, resize: 'vertical', background: '#FFFBF0', border: '1px solid #F6E05E' }}
          value={form.note} onChange={e => set('note', e.target.value)} />
      </LabelInput>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-cerca" onClick={salva} disabled={carico}>
            {carico ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
            Salva modifiche
          </button>
          <button onClick={onCancel} style={{
            padding: '10px 20px', borderRadius: 8, border: '1px solid #dde1e7',
            background: 'white', cursor: 'pointer', fontSize: 14, color: '#718096'
          }}>Annulla</button>
        </div>
        {onElimina && (
          <button type="button" onClick={async () => {
              if (!window.confirm(`Eliminare definitivamente il ricorso ${ricorso.codice_ricorso}? Verranno rimossi scheda, udienze, sentenze e collegamenti. Operazione irreversibile.`)) return;
              try {
                await API.delete(`/finanziario/gestione-ricorso/${ricorso.id}`);
                onElimina();
              } catch (e) {
                window.alert(e.response?.data?.detail || 'Errore nell\'eliminazione.');
              }
            }}
            style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #C53030',
              background: 'white', color: '#C53030', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            🗑 Elimina ricorso
          </button>
        )}
      </div>
    </div>
  );
}

// ── Dettaglio ricorso ─────────────────────────────────────────────────────────

function RicorsiCollegati({ ricorso, tipi, onApriRicorso, onCreaOttemperanza, onProponiOttemperanza }) {
  // ricorso corrente arricchito con i titoli del suo tipo (servono per la direzione appello/ottemperanza)
  const ricorsoConTipo = {
    ...ricorso,
    seg_titoli: (tipi || []).find(t => String(t.id) === String(ricorso.tipo_ricorso_id))?.seg_titoli || null,
  };
  const [dati, setDati] = useState(null);
  const [mostraCollega, setMostraCollega] = useState(false);
  const [cercaColl, setCercaColl] = useState('');
  const [risultatiColl, setRisultatiColl] = useState([]);
  const [soloCliente, setSoloCliente] = useState(true);
  const [caricoColl, setCaricoColl] = useState(false);
  const [erroreColl, setErroreColl] = useState(null);
  const ricarica = () => {
    API.get(`/finanziario/ricorso/${encodeURIComponent(ricorso.codice_ricorso)}/collegati`)
      .then(r => {
        setDati(r.data);
        const generi = generiProponibili(ricorso, r.data?.figli);
        if (generi.length && onProponiOttemperanza) {
          onProponiOttemperanza(ricorso, generi);
        }
      })
      .catch(() => setDati({ padre: null, padri: [], figli: [] }));
  };
  const cercaCollegabili = async (q, filtraCliente) => {
    setCaricoColl(true); setErroreColl(null);
    try {
      let url = `/finanziario/ricorsi-cerca?q=${encodeURIComponent(q)}&limit=30`;
      if (filtraCliente && ricorso.codice_cliente) url += `&codice_cliente=${ricorso.codice_cliente}`;
      const r = await API.get(url);
      setRisultatiColl(r.data.filter(x => x.codice_ricorso !== ricorso.codice_ricorso));
    } catch(e) {
      setErroreColl('Errore nella ricerca.');
    } finally {
      setCaricoColl(false);
    }
  };
  const collega = async (codiceSelezionato, comePadre) => {
    setErroreColl(null);
    try {
      if (comePadre) {
        // il selezionato è il primo grado di questo ricorso
        await API.patch(`/finanziario/ricorso/${encodeURIComponent(ricorso.codice_ricorso)}/collega`,
          { ricorso_padre: codiceSelezionato });
      } else {
        // il selezionato è un derivato: questo ricorso ne diventa il padre
        await API.patch(`/finanziario/ricorso/${encodeURIComponent(codiceSelezionato)}/collega`,
          { ricorso_padre: ricorso.codice_ricorso });
      }
      setMostraCollega(false); setCercaColl(''); setRisultatiColl([]);
      ricarica();
    } catch(e) {
      setErroreColl(e.response?.data?.detail || 'Errore nel collegamento.');
    }
  };
  const scollega = async (voce, isPadre) => {
    if (!window.confirm(`Scollegare ${voce.codice_ricorso}?`)) return;
    setErroreColl(null);
    try {
      const padre = isPadre ? voce.codice_ricorso : ricorso.codice_ricorso;
      const figlio = isPadre ? ricorso.codice_ricorso : voce.codice_ricorso;
      await API.delete(`/finanziario/ricorso/${encodeURIComponent(figlio)}/collegamenti/${encodeURIComponent(padre)}`);
      ricarica();
    } catch(e) {
      setErroreColl(e.response?.data?.detail || 'Errore nello scollegamento.');
    }
  };

  useEffect(() => {
    let attivo = true;
    API.get(`/finanziario/ricorso/${encodeURIComponent(ricorso.codice_ricorso)}/collegati`)
      .then(r => {
        if (!attivo) return;
        setDati(r.data);
        const generi = generiProponibili(ricorso, r.data?.figli);
        if (generi.length && onProponiOttemperanza) {
          onProponiOttemperanza(ricorso, generi);
        }
      })
      .catch(() => { if (attivo) setDati({ padre: null, padri: [], figli: [] }); });
    return () => { attivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ricorso.codice_ricorso]);

  if (!dati) return null;

  const ETICHETTE_GRADO = {'primo_grado':'Primo grado','appello':'Appello','ottemperanza':'Ottemperanza','ottemperanza_ottemperanza':'Ott. di ottemperanza'};
  const COLORI_GRADO = {'primo_grado':'#2E75B6','appello':'#E67E22','ottemperanza':'#8E44AD','ottemperanza_ottemperanza':'#C0392B'};
  const Voce = ({ r, ruolo, isPadre }) => (
    <div onClick={() => onApriRicorso && onApriRicorso(r.codice_ricorso)}
      onMouseEnter={e => e.currentTarget.style.background = '#DFF6EE'}
      onMouseLeave={e => e.currentTarget.style.background = '#F0FBF8'}
      title="Apri la scheda di questo ricorso"
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer',
      background: '#F0FBF8', border: '1px solid #C6EFE3', borderRadius: 8, transition: 'background 0.15s' }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'white', background: COLORI_GRADO[r.grado] || '#16A085',
        padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', flexShrink: 0 }}>{ETICHETTE_GRADO[r.grado] || ruolo}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A2E' }}>{r.tipo || '—'}</span>
        {r.numero_rg && <span style={{ fontSize: 12, color: '#718096', marginLeft: 8 }}>RG {r.numero_rg}</span>}
        {r.numero_sentenza && <span style={{ fontSize: 12, color: '#8E44AD', marginLeft: 8 }}>{etichettaProvvedimento(r)} {r.numero_sentenza}</span>}
        {r.tribunale && <span style={{ fontSize: 12, color: '#a0aec0', marginLeft: 8 }}>{r.tribunale}</span>}
        <div style={{ fontSize: 11, color: '#a0aec0' }}>
          {r.codice_ricorso}
          {(r.cognome || r.nome_cliente) && (
            <span style={{ color: '#2E75B6', marginLeft: 8, fontWeight: 600 }}>
              {(r.cognome || '').toUpperCase()} {r.nome_cliente || ''}
            </span>
          )}
        </div>
      </div>
      {r.stato && <BadgeStato stato={r.stato} />}
      <span style={{ fontSize: 12, color: '#16A085', flexShrink: 0 }}>Apri →</span>
      <button onClick={e => { e.stopPropagation(); scollega(r, !!isPadre); }}
        title="Scollega" style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: '#C53030', fontSize: 15, lineHeight: 1, padding: '0 4px', opacity: 0.6, flexShrink: 0 }}>×</button>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#16A085', textTransform: 'uppercase',
        letterSpacing: '0.8px', marginBottom: 8, paddingBottom: 6,
        borderBottom: '2px solid #16A08520', marginTop: 16, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Ricorsi collegati</span>
        {!mostraCollega && (
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {onCreaOttemperanza && generiProponibili(ricorso, dati?.figli).map(g => (
              <button key={g} onClick={() => onCreaOttemperanza(ricorso, g)}
                style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  border: '1px solid #8E44AD', background: '#F5EEF8', color: '#8E44AD',
                  textTransform: 'none', letterSpacing: 'normal', fontWeight: 600 }}>➕ Crea {GENERI_COLLEGATI[g].etichetta}</button>
            ))}
            <button onClick={() => { setMostraCollega(true); cercaCollegabili('', soloCliente); }}
              style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                border: '1px dashed #16A085', background: 'white', color: '#16A085',
                textTransform: 'none', letterSpacing: 'normal', fontWeight: 500 }}>🔗 Collega ricorso</button>
          </span>
        )}
      </div>
      {erroreColl && (
        <div style={{ fontSize: 12, color: '#C53030', background: '#FCECEA', padding: '6px 10px',
          borderRadius: 6, marginBottom: 8 }}>{erroreColl}</div>
      )}
      {mostraCollega && (
        <div style={{ background: '#F7FDFB', border: '1px solid #C6EFE3', borderRadius: 8,
          padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input className="input-filtro" style={{ flex: 1 }} autoFocus
              placeholder="Cerca per codice ricorso, N.RG, cognome, codice fiscale, n. sentenza, tribunale..."
              value={cercaColl}
              onChange={e => { setCercaColl(e.target.value); cercaCollegabili(e.target.value, soloCliente); }} />
            <label style={{ fontSize: 12, color: '#4a5568', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={soloCliente}
                onChange={e => { setSoloCliente(e.target.checked); cercaCollegabili(cercaColl, e.target.checked); }}
                style={{ marginRight: 4 }} />
              Solo questo cliente
            </label>
            <button onClick={() => { setMostraCollega(false); setCercaColl(''); setRisultatiColl([]); setErroreColl(null); }}
              style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                border: '1px solid #dde1e7', background: 'white', color: '#718096' }}>Annulla</button>
          </div>
          <div style={{ fontSize: 11, color: '#718096', marginBottom: 6 }}>
            Cerca un ricorso: la direzione del collegamento è dedotta dal grado.
          </div>
          {caricoColl && <div style={{ fontSize: 12, color: '#a0aec0' }}>Ricerca...</div>}
          {!caricoColl && risultatiColl.length === 0 && (
            <div style={{ fontSize: 12, color: '#a0aec0' }}>Nessun ricorso trovato.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
            {risultatiColl.map(r => (
              <div key={r.codice_ricorso}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  background: 'white', border: '1px solid #e8eaed', borderRadius: 6 }}>
                <span style={{ padding: '2px 6px', borderRadius: 5, fontSize: 9, fontWeight: 700,
                  background: {'primo_grado':'#2E75B6','appello':'#E67E22','ottemperanza':'#8E44AD','ottemperanza_ottemperanza':'#C0392B'}[r.grado] || '#718096',
                  color: 'white', flexShrink: 0 }}>
                  {(r.grado || '').replace(/_/g, ' ').toUpperCase() || 'N/D'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1A2E' }}>
                    {r.tipo || {'primo_grado':'Primo grado','appello':'Appello','ottemperanza':'Ottemperanza','ottemperanza_ottemperanza':'Ottemperanza di ottemperanza'}[r.grado] || '—'}</span>
                  {r.numero_rg && <span style={{ fontSize: 11, color: '#718096', marginLeft: 6 }}>RG {r.numero_rg}</span>}
                  {r.numero_sentenza && <span style={{ fontSize: 11, color: '#8E44AD', marginLeft: 6 }}>{etichettaProvvedimento(r)} {r.numero_sentenza}{r.tribunale_sentenza ? ` — ${r.tribunale_sentenza}` : ''}</span>}
                  {r.cognome && <div style={{ fontSize: 11, color: '#2E75B6' }}>{r.cognome} {r.nome_cliente} {r.codice_fiscale && <span style={{ color: '#a0aec0' }}>({r.codice_fiscale})</span>}</div>}
                  <div style={{ fontSize: 10, color: '#a0aec0' }}>{r.codice_ricorso}</div>
                </div>
                {r.stato && <BadgeStato stato={r.stato} />}
                {(() => {
                  const giaPadre = (dati.padri || []).some(p => p.codice_ricorso === r.codice_ricorso);
                  const giaFiglio = (dati.figli || []).some(f => f.codice_ricorso === r.codice_ricorso);
                  if (giaPadre || giaFiglio) return <span style={{ fontSize: 11, color: '#a0aec0' }}>già collegato</span>;
                  const dir = direzioneCollegamento(r, ricorsoConTipo);
                  const stBlu = { padding: '3px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                    border: '1px solid #2E75B6', background: '#EBF4FF', color: '#2E75B6', whiteSpace: 'nowrap' };
                  const stVerde = { ...stBlu, border: '1px solid #16A085', background: '#F0FBF8', color: '#16A085' };
                  if (dir === 'padre') return (
                    <button onClick={() => collega(r.codice_ricorso, true)} style={stBlu}
                      title="Direzione dedotta dai gradi: il ricorso selezionato è a monte (padre) di questo">
                      🔗 Collega (è a monte)</button>);
                  if (dir === 'figlio') return (
                    <button onClick={() => collega(r.codice_ricorso, false)} style={stVerde}
                      title="Direzione dedotta dai gradi: il ricorso selezionato è un derivato di questo">
                      🔗 Collega (è un derivato)</button>);
                  return <span title="Due ricorsi dello stesso grado non possono essere collegati"
                    style={{ fontSize: 11, color: '#C05621' }}>stesso grado: non collegabile</span>;
                })()}
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(dati.padri || (dati.padre ? [dati.padre] : [])).map(p => (
          <Voce key={'p-' + p.codice_ricorso} r={p} ruolo="Collegato" isPadre />
        ))}
        {dati.figli.map(f => (
          <Voce key={f.codice_ricorso} r={f} ruolo="Collegato" />
        ))}
        {!(dati.padri || []).length && !dati.padre && dati.figli.length === 0 && !mostraCollega && (
          <div style={{ fontSize: 12, color: '#a0aec0' }}>Nessun ricorso collegato.</div>
        )}
      </div>
    </div>
  );
}

function DettaglioRicorso({ ricorso, tipi, avvocati, parti, onUpdate, onApriRicorso, onOttemperanzaDepositata, onProponiOttemperanza, onNuovoTipo, onElimina }) {
  const [modifica, setModifica] = useState(false);

  if (modifica) {
    return (
      <FormModificaRicorso onNuovoTipo={onNuovoTipo} onElimina={onElimina}
        ricorso={ricorso} tipi={tipi} avvocati={avvocati} parti={parti}
        onSave={async () => { await onUpdate(); setModifica(false); }}
        onCancel={() => setModifica(false)}
      />
    );
  }

  return (
    <div>
      <div style={{ padding: '20px 28px', background: '#FFFFFF' }}>
        <VistaDettaglio ricorso={ricorso} tipi={tipi} avvocati={avvocati} parti={parti}
          onModifica={() => setModifica(true)} onUpdate={onUpdate} />
        <RicorsiCollegati ricorso={ricorso} tipi={tipi} onApriRicorso={onApriRicorso} onCreaOttemperanza={onOttemperanzaDepositata} onProponiOttemperanza={onProponiOttemperanza} />
        <CaricaDocumenti tipo="ricorso" id={ricorso.id}
          titolo={ricorso.collettivo ? 'Documenti del fascicolo processuale' : 'Documenti del fascicolo'} />
      </div>
      <div style={{ padding: '16px 28px', background: '#FAFAFA', borderTop: '1px solid #edf2f7' }}>
        <SezioneRinvii ricorso={ricorso} onUpdate={onUpdate} />
      </div>
      <div style={{ padding: '16px 28px', background: '#F9F8FF', borderTop: '1px solid #edf2f7' }}>
        <SezioneSentenza ricorso={ricorso} onUpdate={onUpdate} onOttemperanzaDepositata={onOttemperanzaDepositata} onProponiOttemperanza={onProponiOttemperanza} />
      </div>
    </div>
  );
}

// ── Card ricorso ──────────────────────────────────────────────────────────────

function RicorsoCard({ ricorso, aperto, onToggle, tipi, avvocati, parti, onUpdate, mostraCliente, onApriRicorso, onOttemperanzaDepositata, onProponiOttemperanza, onNuovoTipo, onElimina, evidenziato }) {
  const fmt = (n) => n ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n) : '—';
  const gradoColore = {
    'primo_grado': '#2E75B6', 'appello': '#E67E22',
    'ottemperanza': '#8E44AD', 'ottemperanza_ottemperanza': '#C0392B'
  };

  return (
    <div style={{ background: evidenziato ? '#FFFDF2' : 'white',
      border: evidenziato ? '2px solid #D69E2E' : '1px solid #e8eaed', borderRadius: 12, marginBottom: 12, overflow: 'hidden',
      boxShadow: evidenziato ? '0 0 0 4px rgba(214,158,46,0.18), 0 1px 4px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.06)' }}>
      {evidenziato && (
        <div style={{ background: '#D69E2E', color: 'white', fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
          textTransform: 'uppercase', padding: '3px 20px' }}>
          ◎ Risultato della ricerca
        </div>
      )}
      <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flex: 1 }}
          onClick={onToggle}>
          <span style={{
            padding: '8px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
            background: gradoColore[ricorso.grado] || '#718096', color: 'white', flexShrink: 0,
            minWidth: 110, textAlign: 'center', whiteSpace: 'nowrap', boxSizing: 'border-box'
          }}>{ricorso.grado?.replace(/_/g, ' ').toUpperCase()}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 400, fontSize: 13, color: '#1A1A2E' }}>
              {ricorso.tipo || ricorso.descrizione}
              {ricorso.numero_rg && <span style={{ color: '#1A1A2E', marginLeft: 8, fontSize: 13, fontWeight: 700 }}>RG {ricorso.numero_rg}</span>}
              {ricorso.tribunale && <span style={{ color: '#718096', marginLeft: 8, fontSize: 12 }}>{ricorso.tribunale}</span>}
              {ricorso.numero_sentenza && <span style={{ color: '#8E44AD', marginLeft: 8, fontSize: 12, fontWeight: 600 }}>{etichettaProvvedimento(ricorso)} {ricorso.numero_sentenza}</span>}
              {ricorso.ultima_udienza && (!ricorso.numero_sentenza || udienzaFutura(ricorso.ultima_udienza)) && (() => {
                const ok = !!ricorso.ultima_udienza_notificata;
                return (
                  <span title={ok ? 'Udienza notificata' : 'Udienza da notificare'}
                    style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: ok ? '#EBF9F1' : '#FFF7ED', color: ok ? '#276749' : '#C05621',
                      border: `1px solid ${ok ? '#9AE6B4' : '#FBD38D'}` }}>
                    {ricorso.ultima_is_rinvio ? 'Rinvio al' : 'Udienza'} {fmtDataBadge(ricorso.ultima_udienza)} · {ok ? 'notificata' : 'da notificare'}
                  </span>
                );
              })()}
              {ricorso.numero_sentenza && (() => {
                const ok = !!ricorso.data_notifica_sentenza && !ricorso.notifica_no;
                return (
                  <span title={ok ? 'Provvedimento notificato' : 'Provvedimento non notificato'}
                    style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: ok ? '#EBF9F1' : '#EDF2F7', color: ok ? '#276749' : '#718096',
                      border: `1px solid ${ok ? '#9AE6B4' : '#E2E8F0'}` }}>
                    {ok ? `notificata ${fmtDataBadge(ricorso.data_notifica_sentenza)}` : 'non notificata'}
                  </span>
                );
              })()}
            </div>
            {mostraCliente && ricorso.cognome && (
              <div style={{ fontSize: 12, fontWeight: 500, color: '#2E75B6', marginTop: 2 }}>
                {intestazioneCollettivo(ricorso)}
              </div>
            )}
            <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>
              {ricorso.codice_ricorso}
              {(ricorso.desc_ricorso || ricorso.descrizione) && (ricorso.desc_ricorso || ricorso.descrizione) !== ricorso.codice_ricorso && (
                <span style={{ color: '#A0AEC0' }}> · {ricorso.desc_ricorso || ricorso.descrizione}</span>
              )}
              {ricorso.collettivo && (
                <span style={{ marginLeft: 8, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <BadgeCollettivo ricorso={ricorso} />
                  {ricorso.stato_adesione_cliente && (
                    <span title="Adesione del cliente">adesione: <BadgeStatoAdesione stato={ricorso.stato_adesione_cliente} /></span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <BadgeStato stato={ricorso.stato} />
          <BadgePendente ricorso={ricorso} />
          {ricorso.esito && (
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              color: (ricorso.esito || '').toLowerCase().includes('accolt') ? '#276749'
                : (ricorso.esito || '').toLowerCase().includes('rigett') ? '#C53030' : '#718096' }}>
              {ricorso.esito}
            </span>
          )}
          {ricorso.importo_spese && (
            <span style={{ fontSize: 13, fontWeight: 500, color: ricorso.liquidato ? '#276749' : '#c53030' }}>
              {fmt(ricorso.importo_spese)}
            </span>
          )}
          <button onClick={(e) => { e.stopPropagation(); }}
            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #dde1e7',
              background: 'white', cursor: 'pointer', fontSize: 11, color: '#718096', display: 'none' }}>
          </button>
          <div style={{ cursor: 'pointer' }} onClick={onToggle}>
            {aperto ? <ChevronUp size={16} color="#718096" /> : <ChevronDown size={16} color="#718096" />}
          </div>
        </div>
      </div>
      {aperto && (
        <div style={{ borderTop: '1px solid #f0f0f0' }}>
          <DettaglioRicorso ricorso={ricorso} tipi={tipi} avvocati={avvocati} parti={parti} onUpdate={onUpdate} onApriRicorso={onApriRicorso} onOttemperanzaDepositata={onOttemperanzaDepositata} onProponiOttemperanza={onProponiOttemperanza} onNuovoTipo={onNuovoTipo} onElimina={onElimina} />
        </div>
      )}
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function SchedaRicorso() {
  const { haPermesso } = useAuth();
  const [proponiOtt, setProponiOtt] = useState(null);
  const segnalaProposte = (r, generi) => setProponiOtt({ ricorso: r, generi });
  const nuovoTipo = (grado) => creaTipoAlVolo(API, async () => {
    const t = await API.get('/finanziario/tipi-ricorso');
    setTipi(t.data);
  }, grado);
  const avviaCreazioneOttemperanza = async (r, genere = 'ott_ricorrente') => {
    let tipoId = null;
    let clienteForm = {
      codice_cliente: r.codice_cliente, cognome: r.cognome, nome: r.nome,
      codice_fiscale: r.codice_fiscale, email: r.email
    };
    let grado = 'ottemperanza';
    if (genere === 'ott_ricorrente') {
      const t = (tipi || []).find(x => (x.nome || '').toLowerCase().includes('ottemperanza ricorrente'))
        || (tipi || []).find(x => (x.nome || '').toLowerCase().includes('ottemperanza'));
      tipoId = t?.id || null;
    } else if (genere === 'ott_spese') {
      const t = (tipi || []).find(x => (x.nome || '').toLowerCase().includes('spese'));
      tipoId = t?.id || null;
      clienteForm = { codice_cliente: 47832, cognome: 'MARONE', nome: 'GUIDO', codice_fiscale: 'MRNGDU78L18F839D', email: '' };
    } else if (genere === 'appello') {
      grado = 'appello';
      const tipoPadre = (tipi || []).find(x => String(x.id) === String(r.tipo_ricorso_id))?.nome || '';
      const nomeAppello = tipoPadre.toLowerCase().startsWith('appello') ? tipoPadre : `Appello ${tipoPadre}`.trim();
      try {
        const resp = await API.post('/finanziario/tipi-ricorso', { nome: nomeAppello });
        tipoId = resp.data.id;
        if (resp.data.creato) {
          const t = await API.get('/finanziario/tipi-ricorso');
          setTipi(t.data);
        }
      } catch (e) {
        tipoId = null;
      }
    }
    setClienteTrovato(prev => prev && prev.codice_cliente === clienteForm.codice_cliente ? prev : clienteForm);
    setPrecompilato({
      // l'appello di un collettivo si crea non depositato (i ricorrenti si scelgono prima del deposito)
      tipo_ricorso_id: tipoId, grado, stato: r.collettivo && genere === 'appello' ? 'in_lavorazione' : 'depositato', origine: 'derivato',
      data_registrazione: new Date().toISOString().substring(0, 10),
      legale_studio_id: r.legale_studio_id || null, parte_resistente_id: r.parte_resistente_id || null,
      _padre: r.codice_ricorso,
      // appello di un collettivo: collettivo anche lui, con i ricorrenti scelti tra i depositati
      ...(r.collettivo && genere === 'appello' ? { collettivo: true, _padreCollettivo: r.id } : {})
    });
    setProponiOtt(null); setMostraForm(true);
    setTimeout(() => document.getElementById('form-nuovo-ricorso')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };
  const [precompilato, setPrecompilato] = useState(null);
  const [cerca, setCerca] = useState('');
  const [clienteTrovato, setClienteTrovato] = useState(null);
  const [clientiMultipli, setClientiMultipli] = useState([]);
  const [ricorsi, setRicorsi] = useState([]);
  const [carico, setCarico] = useState(false);
  const [errore, setErrore] = useState(null);
  const [ricorsoAperto, setRicorsoAperto] = useState(null);
  const [mostraForm, setMostraForm] = useState(false);
  const [tipi, setTipi] = useState([]);
  const [avvocati, setAvvocati] = useState([]);
  const [parti, setParti] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroGrado, setFiltroGrado] = useState('');
  const [filtroRg, setFiltroRg] = useState('');
  const [filtroProvvedimento, setFiltroProvvedimento] = useState('');
  const [filtroTribunale, setFiltroTribunale] = useState('');
  const [suggerimentiTribunali, setSuggerimentiTribunali] = useState([]);
  const [ricorsiGlobali, setRicorsiGlobali] = useState([]);
  const [paginaG, setPaginaG] = useState(1);
  const [pagineTotaliG, setPagineTotaliG] = useState(1);
  const [totaleG, setTotaleG] = useState(0);
  const PER_PAGINA_G = 25;
  const [cercaGlobale] = useState('');
  const [modalitaGlobale, setModalitaGlobale] = useState(true);
  const [vistaAlbero, setVistaAlbero] = useState(() => sessionStorage.getItem('ricorsi_vista') === 'albero');
  const vistaAlberoRef = React.useRef(vistaAlbero);
  const [famiglieG, setFamiglieG] = useState(null);
  const [alberoCliente, setAlberoCliente] = useState(null);   // {famiglie, risultati} per la vista cliente
  const endpointRicorsi = () => (vistaAlberoRef.current ? '/finanziario/ricorsi-albero' : '/finanziario/ricorsi');
  const cambiaVista = (albero) => {
    vistaAlberoRef.current = albero; setVistaAlbero(albero);
    sessionStorage.setItem('ricorsi_vista', albero ? 'albero' : 'elenco');
  };
  const [mostraAnagrafica, setMostraAnagrafica] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/finanziario/tipi-ricorso'),
      API.get('/finanziario/avvocati'),
      API.get('/finanziario/parti-resistenti'),
    ]).then(([t, a, p]) => {
      setTipi(t.data);
      setAvvocati(a.data.map(av => ({ ...av, nome: `${av.cognome} ${av.nome}` })));
      setParti(p.data);
    });
  }, []);

  const apriRicorsoCollegato = async (codice) => {
    setCarico(true); setErrore(null);
    try {
      const params = new URLSearchParams({ pagina: 1, per_pagina: PER_PAGINA_G, q: codice });
      const r = await API.get(`${endpointRicorsi()}?${params}`);
      setRicorsiGlobali(r.data.risultati);
      setFamiglieG(r.data.famiglie || null);
      setTotaleG(r.data.totale);
      setPagineTotaliG(r.data.pagine_totali);
      setPaginaG(1);
      setFiltroTipo(''); setFiltroStato(''); setFiltroGrado('');
      setModalitaGlobale(true);
      setClienteTrovato(null);
      setClientiMultipli([]);
      const esatto = r.data.risultati.find(x => x.codice_ricorso === codice) || r.data.risultati[0];
      setRicorsoAperto(esatto ? esatto.id : null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch(e) {
      setErrore('Errore nell\'apertura del ricorso collegato.');
    } finally {
      setCarico(false);
    }
  };

  const cercaGlobaleHandler = async (p = 1) => {
    setCarico(true); setErrore(null);
    try {
      const params = new URLSearchParams({ pagina: p, per_pagina: PER_PAGINA_G });
      if (filtroTipo) params.append('tipo', filtroTipo);
      if (filtroStato) params.append('stato', filtroStato);
      if (filtroGrado) params.append('grado', filtroGrado);
      if (cercaGlobale) params.append('q', cercaGlobale);
      if (filtroRg.trim()) params.append('rg', filtroRg.trim());
      if (filtroProvvedimento.trim()) params.append('provvedimento', filtroProvvedimento.trim());
      if (filtroTribunale.trim()) params.append('tribunale', filtroTribunale.trim());
      const r = await API.get(`${endpointRicorsi()}?${params}`);
      setRicorsiGlobali(r.data.risultati);
      setFamiglieG(r.data.famiglie || null);
      setTotaleG(r.data.totale);
      setPagineTotaliG(r.data.pagine_totali);
      setPaginaG(p);
      setModalitaGlobale(true);
      setClienteTrovato(null);
    } catch(e) {
      setErrore('Errore nella ricerca.');
    } finally {
      setCarico(false);
    }
  };

  // Ricerca automatica: parte ~mezzo secondo dopo l'ultima modifica di un filtro
  const filtriAttivi = !!(filtroTipo || filtroStato || filtroGrado
    || filtroRg.trim() || filtroProvvedimento.trim() || filtroTribunale.trim());
  useEffect(() => {
    if (!filtriAttivi) return undefined;
    const t = setTimeout(() => cercaGlobaleHandler(1), 450);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTipo, filtroStato, filtroGrado, filtroRg, filtroProvvedimento, filtroTribunale]);

  // Suggerimenti per il filtro Tribunale (anagrafica tribunali)
  useEffect(() => {
    const q = filtroTribunale.trim();
    if (q.length < 2) { setSuggerimentiTribunali([]); return undefined; }
    let attivo = true;
    const t = setTimeout(() => {
      API.get(`/finanziario/tribunali?${new URLSearchParams({ q })}`)
        .then(r => { if (attivo) setSuggerimentiTribunali(r.data || []); })
        .catch(() => {});
    }, 250);
    return () => { attivo = false; clearTimeout(t); };
  }, [filtroTribunale]);

  const cercaCliente = async () => {
    if (!cerca.trim()) return;
    setCarico(true); setErrore(null); setClienteTrovato(null); setRicorsi([]);
    try {
      const rc = await API.get(`/clienti?q=${cerca}`);
      const trovati = rc.data.risultati || [];
      setClientiMultipli([]);
      if (trovati.length === 1) {
        setClienteTrovato(trovati[0]);
        const r = await getRicorsiCliente(trovati[0].codice_cliente);
        setRicorsi(r.data);
      } else if (trovati.length > 1) {
        setClientiMultipli(trovati);
      } else {
        // Fallback: prova ricerca globale ricorsi (per codice ricorso, NRG, ecc.)
        const rg = await API.get(`${endpointRicorsi()}?${new URLSearchParams({ pagina: 1, per_pagina: PER_PAGINA_G, q: cerca })}`);
        if (rg.data.risultati && rg.data.risultati.length > 0) {
          setRicorsiGlobali(rg.data.risultati);
          setFamiglieG(rg.data.famiglie || null);
          setTotaleG(rg.data.totale);
          setPagineTotaliG(rg.data.pagine_totali);
          setPaginaG(1);
          setModalitaGlobale(true);
          const esatto = rg.data.risultati.find(x => (x.codice_ricorso || '').toUpperCase() === cerca.trim().toUpperCase());
          setRicorsoAperto(esatto ? esatto.id : (rg.data.risultati.length === 1 ? rg.data.risultati[0].id : null));
        } else {
          setErrore('Nessun risultato. Verifica nome, codice fiscale o codice ricorso.');
        }
      }
    } catch(e) {
      setErrore('Errore nella ricerca cliente.');
    } finally {
      setCarico(false);
    }
  };

  // Vista cliente in modalità Albero: carica le famiglie del cliente (si aggiorna quando cambiano i suoi ricorsi)
  useEffect(() => {
    if (modalitaGlobale || !clienteTrovato || !vistaAlbero) { setAlberoCliente(null); return undefined; }
    let attivo = true;
    API.get(`/finanziario/ricorsi-albero?${new URLSearchParams({ codice_cliente: clienteTrovato.codice_cliente, per_pagina: 100 })}`)
      .then(r => { if (attivo) setAlberoCliente({ famiglie: r.data.famiglie || [], risultati: r.data.risultati || [] }); })
      .catch(() => { if (attivo) setAlberoCliente(null); });
    return () => { attivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalitaGlobale, clienteTrovato?.codice_cliente, ricorsi, vistaAlbero]);

  // Vista Albero globale: evidenzia i ricorsi che soddisfano la ricerca, ma solo nelle famiglie
  // dove compaiono anche ricorsi di contesto (padri/derivati non cercati); altrimenti non c'e' nulla da distinguere
  const codiciEvidenziati = new Set();
  if (modalitaGlobale && vistaAlbero && famiglieG) {
    famiglieG.forEach(f => {
      const nodi = f.nodi || [];
      if (nodi.some(n => !n.trovato)) nodi.forEach(n => { if (n.trovato) codiciEvidenziati.add(n.codice); });
    });
  }

  const listaFiltrata = modalitaGlobale ? ricorsiGlobali : ricorsi.filter(r => {
    if (filtroTipo && r.tipo !== filtroTipo) return false;
    if (filtroStato && r.stato !== filtroStato) return false;
    if (filtroGrado && r.grado !== filtroGrado) return false;
    return true;
  });

  return (
    <div style={{ maxWidth: '100%', width: '100%' }}>
      {/* Barra ricerca + filtri sempre visibili */}
      <div style={{ background: 'white', border: '1px solid #e8eaed', borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input className="input-principale" style={{ flex: 1 }}
            placeholder="Cerca per cognome, nome, codice fiscale, codice ricorso o N. RG..."
            value={cerca}
            onChange={e => { setCerca(e.target.value); setModalitaGlobale(false); }}
            onKeyDown={e => e.key === 'Enter' && cercaCliente()} />
          <button className="btn-cerca" onClick={cercaCliente} disabled={carico}>
            {carico ? <Loader size={16} className="spin" /> : <Search size={16} />}
            Cerca
          </button>
          {haPermesso('ricorsi.crea') && (
            <button className="btn-cerca" style={{ background: '#276749' }}
              title={clienteTrovato ? `Nuovo ricorso per ${clienteTrovato.cognome || ''} ${clienteTrovato.nome || ''}` : 'Nuovo ricorso: il cliente si sceglie nel form'}
              onClick={() => {
                setPrecompilato(null);
                setMostraForm(true);
                setTimeout(() => document.getElementById('form-nuovo-ricorso')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
              }}>
              <Plus size={16} /> Nuovo ricorso
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, alignItems: 'center' }}>
          {/* riga 1: tipo, stato, grado; riga 2: N. RG, numero provvedimento, tribunale (23/10) */}
          <AutocompleteTipo tipi={tipi} value={filtroTipo} onChange={setFiltroTipo} />
          <select className="input-filtro" style={{ width: '100%' }}
            value={filtroStato} onChange={e => setFiltroStato(e.target.value)}>
            <option value="">Tutti gli stati</option>
            <option value="sentenza_pubblicata">Con sentenza pubblicata</option>
            <option value="attesa_sentenza">In attesa di sentenza</option>
            <option value="in_lavorazione">In lavorazione</option>
            <option value="da_depositare">Da depositare</option>
            <option value="depositato">Depositato</option>
            <option value="chiuso">Chiuso</option>
            <option value="estinto">Estinto</option>
            <option value="estinto_e_ripresentato">Estinto e ripresentato</option>
            <option value="revocato">Revocato</option>
          </select>
          <select className="input-filtro" style={{ width: '100%' }}
            value={filtroGrado} onChange={e => setFiltroGrado(e.target.value)}>
            <option value="">Tutti i gradi</option>
            <option value="primo_grado">Primo grado</option>
            <option value="appello">Appello</option>
          </select>
          <input className="input-filtro" style={{ width: '100%' }}
            value={filtroRg} onChange={e => setFiltroRg(e.target.value)}
            placeholder="N. RG" title="Anche parziale, es. 314 o 314/26" />
          <input className="input-filtro" style={{ width: '100%' }}
            value={filtroProvvedimento} onChange={e => setFiltroProvvedimento(e.target.value)}
            placeholder="Numero provvedimento" title="Numero della sentenza o del provvedimento, anche parziale" />
          <input className="input-filtro" style={{ width: '100%' }}
            value={filtroTribunale} onChange={e => setFiltroTribunale(e.target.value)}
            placeholder="Tribunale" list="suggerimenti-tribunali" title="Nome o città del tribunale" />
          <datalist id="suggerimenti-tribunali">
            {suggerimentiTribunali.map(t => <option key={t.id} value={t.nome} />)}
          </datalist>
          {filtriAttivi && (
            <button onClick={() => { setFiltroTipo(''); setFiltroStato(''); setFiltroGrado(''); setFiltroRg(''); setFiltroProvvedimento(''); setFiltroTribunale(''); setRicorsiGlobali([]); setModalitaGlobale(false); }}
              style={{ gridColumn: '1 / -1', justifySelf: 'end', fontSize: 12, color: '#c53030', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px' }}>
              ✕ Rimuovi filtri
            </button>
          )}
        </div>
      </div>

      {errore && <div className="alert errore"><AlertCircle size={16} /><span>{errore}</span></div>}

      {clientiMultipli.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #e8eaed', borderRadius: 10,
          padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: '#718096', marginBottom: 10 }}>
            {clientiMultipli.length} clienti trovati — seleziona quello giusto:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {clientiMultipli.map(c => (
              <div key={c.codice_cliente}
                onClick={async () => {
                  setClientiMultipli([]);
                  setClienteTrovato(c);
                  setCarico(true);
                  try {
                    const r = await getRicorsiCliente(c.codice_cliente);
                    setRicorsi(r.data);
                  } finally { setCarico(false); }
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderRadius: 8, border: '1px solid #edf2f7',
                  background: '#F7F8FA', cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#EBF4FF'}
                onMouseLeave={e => e.currentTarget.style.background = '#F7F8FA'}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E' }}>
                    {c.tipo_cliente === 'azienda' ? c.ragione_sociale : `${c.cognome} ${c.nome}`}
                  </span>
                  {c.codice_fiscale && <span style={{ fontSize: 12, color: '#718096', marginLeft: 10 }}>CF: {c.codice_fiscale}</span>}
                  {c.localita && <span style={{ fontSize: 12, color: '#a0aec0', marginLeft: 10 }}>{c.localita}{c.provincia ? ` (${c.provincia})` : ''}</span>}
                </div>
                <span style={{ fontSize: 12, color: '#2E75B6' }}>Seleziona →</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {clienteTrovato && (
        <div style={{
          background: '#EBF4FF', border: '1px solid #BEE3F8', borderRadius: 10,
          padding: '12px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: 15, color: '#1A1A2E' }}>
              {clienteTrovato.cognome} {clienteTrovato.nome}
            </span>
            <span style={{ fontSize: 12, color: '#718096', marginLeft: 12 }}>CF: {clienteTrovato.codice_fiscale}</span>
            {clienteTrovato.email && <span style={{ fontSize: 12, color: '#718096', marginLeft: 12 }}>{clienteTrovato.email}</span>}
          </div>
        </div>
      )}

      {clienteTrovato && (
        <div style={{ background: 'white', border: '1px solid #e8eaed', borderRadius: 12,
          marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', cursor: 'pointer', background: mostraAnagrafica ? '#F7F8FA' : 'white' }}
            onClick={() => setMostraAnagrafica(!mostraAnagrafica)}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#2E75B6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              📋 Scheda anagrafica
            </span>
            <span style={{ fontSize: 12, color: '#718096' }}>{mostraAnagrafica ? '▲' : '▼'}</span>
          </div>
          {mostraAnagrafica && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid #f0f0f0' }}>
              <SchedaCliente
                cliente={clienteTrovato}
                onAggiornamento={(c) => setClienteTrovato(c)}
              />
            </div>
          )}
        </div>
      )}

      {proponiOtt && (
        <div style={{ background: '#EEF6FF', border: '1px solid #2E75B6', borderRadius: 10, padding: 16, marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          position: 'sticky', top: 8, zIndex: 60, boxShadow: '0 4px 14px rgba(46,117,182,0.25)' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 600, color: '#1A1A2E' }}>
              Ricorsi collegati proponibili per {proponiOtt.ricorso.codice_ricorso}
            </div>
            <div style={{ fontSize: 13, color: '#4A5568' }}>Registra ora i ricorsi collegati suggeriti dalla scheda.</div>
          </div>
          {(proponiOtt.generi || []).map(g => (
            <button key={g} className="btn-cerca"
              onClick={() => avviaCreazioneOttemperanza(proponiOtt.ricorso, g)}>
              Crea {GENERI_COLLEGATI[g].etichetta}
            </button>
          ))}
          <button onClick={() => setProponiOtt(null)} style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}>Più tardi</button>
        </div>
      )}
      {mostraForm && (
        <div id="form-nuovo-ricorso" />
      )}
      {mostraForm && (
        <FormNuovoRicorso
          key={clienteTrovato?.codice_cliente || 'senza-cliente'}
          cliente={clienteTrovato}
          tipi={tipi} avvocati={avvocati} parti={parti} onNuovoTipo={haPermesso('configurazione.gestisci') ? nuovoTipo : undefined}
          iniziale={precompilato ? Object.fromEntries(Object.entries(precompilato).filter(([k]) => !k.startsWith('_'))) : null}
          padreCollettivo={precompilato?._padreCollettivo}
          onSave={async (data) => {
            const intestatario = data._intestatarioObj || clienteTrovato;
            const codCliente = data._intestatario || clienteTrovato?.codice_cliente;
            delete data._intestatario;
            delete data._intestatarioObj;
            // appello di un collettivo: scheda, collegamento e ricorrenti in una sola chiamata
            const conPadre = precompilato?._padreCollettivo ? { ricorso_padre: precompilato._padre } : {};
            const resp = await creaGestioneRicorso({ ...data, ...conPadre, codice_cliente: codCliente });
            // il codice generato dall'app arriva dalla risposta (nel form e' null quando non e' inserito a mano)
            const codiceCreato = resp?.data?.codice_ricorso || data.codice_ricorso;
            if (precompilato?._padre && !conPadre.ricorso_padre && codiceCreato) {
              try {
                await API.patch(`/finanziario/ricorso/${encodeURIComponent(codiceCreato)}/collega`, { ricorso_padre: precompilato._padre });
              } catch (e) { console.error('Collegamento al ricorso padre non riuscito', e); }
            }
            // con un cliente gia' aperto si ricaricano i suoi ricorsi (come prima);
            // senza cliente si apre la vista del cliente intestatario appena scelto
            const clienteVista = clienteTrovato || intestatario;
            const r = await getRicorsiCliente(clienteVista.codice_cliente);
            if (!clienteTrovato) { setClienteTrovato(intestatario); setModalitaGlobale(false); }
            setRicorsi(r.data);
            setMostraForm(false); setPrecompilato(null);
          }}
          onCancel={() => { setMostraForm(false); setPrecompilato(null); }}
        />
      )}

      {/* Lista ricorsi — globale o per cliente */}
      {(listaFiltrata.length > 0 || modalitaGlobale) && (
        <div>
          {listaFiltrata.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 13, color: '#718096' }}>
                {modalitaGlobale && vistaAlbero && famiglieG
                  ? <>{totaleG} famigli{totaleG !== 1 ? 'e' : 'a'} di ricorsi</>
                  : <>{modalitaGlobale ? totaleG : listaFiltrata.length} ricorso{(modalitaGlobale ? totaleG : listaFiltrata.length) !== 1 ? 'i' : ''} trovato{(modalitaGlobale ? totaleG : listaFiltrata.length) !== 1 ? 'i' : ''}</>}
                {modalitaGlobale && <span style={{ marginLeft: 8, color: '#2E75B6' }}>— vista globale</span>}
                {(
                  <span style={{ marginLeft: 12, display: 'inline-flex', border: '1px solid #dde1e7', borderRadius: 6, overflow: 'hidden', verticalAlign: 'middle' }}>
                    {[['elenco', 'Elenco', false], ['albero', 'Albero', true]].map(([k, label, val]) => (
                      <button key={k} onClick={() => { if (vistaAlbero !== val) { cambiaVista(val); if (modalitaGlobale) cercaGlobaleHandler(paginaG); } }}
                        title={val ? 'Ricorsi raggruppati per famiglia (primo grado e derivati)' : 'Elenco piatto'}
                        style={{ padding: '3px 10px', fontSize: 11, border: 'none', cursor: 'pointer',
                          background: vistaAlbero === val ? '#2E75B6' : 'white', color: vistaAlbero === val ? 'white' : '#4A5568', fontWeight: 600 }}>
                        {label}
                      </button>
                    ))}
                  </span>
                )}
                {modalitaGlobale && pagineTotaliG > 1 && ` — pagina ${paginaG} di ${pagineTotaliG}`}
              </div>
              {modalitaGlobale && pagineTotaliG > 1 && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => cercaGlobaleHandler(paginaG - 1)} disabled={paginaG === 1}
                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #dde1e7',
                      background: paginaG === 1 ? '#f7f8fa' : 'white', cursor: paginaG === 1 ? 'not-allowed' : 'pointer',
                      fontSize: 13, color: paginaG === 1 ? '#a0aec0' : '#1A1A2E' }}>← Precedente</button>
                  {Array.from({ length: Math.min(5, pagineTotaliG) }, (_, i) => {
                    const p = Math.max(1, Math.min(paginaG - 2, pagineTotaliG - 4)) + i;
                    if (p > pagineTotaliG) return null;
                    return (
                      <button key={p} onClick={() => cercaGlobaleHandler(p)}
                        style={{ padding: '6px 11px', borderRadius: 6,
                          border: `1px solid ${p === paginaG ? '#2E75B6' : '#dde1e7'}`,
                          background: p === paginaG ? '#EBF4FF' : 'white',
                          color: p === paginaG ? '#2E75B6' : '#1A1A2E',
                          cursor: 'pointer', fontSize: 13,
                          fontWeight: p === paginaG ? 600 : 400 }}>{p}</button>
                    );
                  })}
                  <button onClick={() => cercaGlobaleHandler(paginaG + 1)} disabled={paginaG === pagineTotaliG}
                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #dde1e7',
                      background: paginaG === pagineTotaliG ? '#f7f8fa' : 'white',
                      cursor: paginaG === pagineTotaliG ? 'not-allowed' : 'pointer',
                      fontSize: 13, color: paginaG === pagineTotaliG ? '#a0aec0' : '#1A1A2E' }}>Successiva →</button>
                </div>
              )}
            </div>
          )}
          {vistaAlbero && ((modalitaGlobale && famiglieG) || (!modalitaGlobale && alberoCliente)) ? (
            <AlberoRicorsi famiglie={modalitaGlobale ? famiglieG : alberoCliente.famiglie}
              risultati={modalitaGlobale ? listaFiltrata : alberoCliente.risultati} onApriRicorso={apriRicorsoCollegato}
              renderCard={(r) => (
                <RicorsoCard onOttemperanzaDepositata={avviaCreazioneOttemperanza} onProponiOttemperanza={segnalaProposte} onNuovoTipo={haPermesso('configurazione.gestisci') ? nuovoTipo : undefined}
                  onElimina={haPermesso('ricorsi.elimina') ? async () => { const rr = await getRicorsiCliente(clienteTrovato.codice_cliente); setRicorsi(rr.data); } : undefined}
                  key={r.id} ricorso={!modalitaGlobale && r.collettivo
                    ? { ...r, stato_adesione_cliente: ricorsi.find(x => x.id === r.id)?.stato_adesione_cliente } : r}
                  evidenziato={codiciEvidenziati.has(r.codice_ricorso)}
                  aperto={ricorsoAperto === r.id}
                  onToggle={() => setRicorsoAperto(ricorsoAperto === r.id ? null : r.id)}
                  tipi={tipi} avvocati={avvocati} parti={parti}
                  mostraCliente={true}
                  onApriRicorso={apriRicorsoCollegato}
                  onUpdate={async () => {
                    if (modalitaGlobale) {
                      await cercaGlobaleHandler(paginaG);
                    } else if (clienteTrovato) {
                      const updated = await getRicorsiCliente(clienteTrovato.codice_cliente);
                      setRicorsi(updated.data);
                    }
                  }} />
              )} />
          ) : listaFiltrata.map(r => (
            <RicorsoCard onOttemperanzaDepositata={avviaCreazioneOttemperanza} onProponiOttemperanza={segnalaProposte} onNuovoTipo={haPermesso('configurazione.gestisci') ? nuovoTipo : undefined}
              onElimina={haPermesso('ricorsi.elimina') ? async () => { const rr = await getRicorsiCliente(clienteTrovato.codice_cliente); setRicorsi(rr.data); } : undefined}
              key={r.id} ricorso={r}
              aperto={ricorsoAperto === r.id}
              onToggle={() => setRicorsoAperto(ricorsoAperto === r.id ? null : r.id)}
              tipi={tipi} avvocati={avvocati} parti={parti}
              mostraCliente={modalitaGlobale}
              onApriRicorso={apriRicorsoCollegato}
              onUpdate={async () => {
                if (modalitaGlobale) {
                  await cercaGlobaleHandler(paginaG);
                } else if (clienteTrovato) {
                  const updated = await getRicorsiCliente(clienteTrovato.codice_cliente);
                  setRicorsi(updated.data);
                }
              }} />
          ))}
        </div>
      )}
    </div>
  );
}

// per i test dei form (SchedaRicorso.test.js)
export { FormNuovoRicorso, FormModificaRicorso, campiCollettivo, VistaDettaglio, RicorsoCard, SezioneSentenza, creaTipoAlVolo };
